// src/ReaderView.js – Reader View (SERVER TRUTH ONLY)
// Build: 2026-05-29-readerview-published-cards-v3
//
// Preserves:
// - Deep-link support /reader-view?storyId=...
// - Tracking deliberate action only
// - Shelf awareness + status hard-fix
// - Step 3 guidance line
// - Step 5 skeleton loaders for left list and right panel while loading
//
// Step 6 (NEW):
// - Right panel shows skeleton while list is still loading (prevents “blank info” feel)
// - Tighter, steadier card rhythm (no jittery spacing, calmer list items)
// - Buttons feel consistent (min heights, clearer disabled state)
// - Selected card highlight reads stronger and stays clean
//
// Step 7 (NEW):
// - Reader rating social proof: public average + rating count near user stars
// - Empty-state invitation for first ratings, without making new scenes look abandoned

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { useUser } from "./UserContext";
import ProjectBreadcrumbTrail from "./components/ProjectBreadcrumbTrail";
import ChildrensBookManuscriptReadOnly from "./components/ChildrensBookManuscriptReadOnly";
import { api } from "./api";
import { openingSceneExcerpt } from "./utils/readerOpeningSceneExcerpt";

import { TEXT } from "./i18n";
import { getPublicationInfo as getKnownPublicationInfo, getPublishedAuthorName } from "./utils/publicationStatus";

const READER_LIST_PAGE_SIZE = 4;
const READER_LIST_LIMIT = 80;
const STORY_CACHE_LIMIT = 80;


function normalizeStoriesListResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.stories)) return payload.stories;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function buildStoryCacheKey(storyLike) {
  const id = String(storyLike?._id || storyLike?.id || "").trim();
  return id || "";
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function formatRelativeTime(date, lang = "en") {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";

  const isFR = String(lang || "en").toLowerCase() === "fr";
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);

  if (diff < 60) return isFR ? "à l’instant" : "just now";

  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    if (isFR) return mins === 1 ? "il y a 1 min" : `il y a ${mins} min`;
    return mins === 1 ? "1 min ago" : `${mins} mins ago`;
  }

  if (diff < 86400) {
    const hrs = Math.floor(diff / 3600);
    if (isFR) return hrs === 1 ? "il y a 1 h" : `il y a ${hrs} h`;
    return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
  }

  const days = Math.floor(diff / 86400);
  if (days < 30) {
    if (isFR) return days === 1 ? "il y a 1 jour" : `il y a ${days} jours`;
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    if (isFR) return months === 1 ? "il y a 1 mois" : `il y a ${months} mois`;
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }

  const years = Math.floor(days / 365);
  if (isFR) return years === 1 ? "il y a 1 an" : `il y a ${years} ans`;
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

function formatTrackingLastOpened(trackingPayload, lang = "en", t = {}) {
  const lastOpenedAt =
    trackingPayload?.tracking?.lastOpenedAt ||
    trackingPayload?.lastOpenedAt ||
    "";

  if (!lastOpenedAt) {
    return t.trackingNotOpenedYet || (String(lang || "en").toLowerCase() === "fr" ? "Pas encore ouvert" : "Not opened yet");
  }

  const rel = formatRelativeTime(lastOpenedAt, lang);
  const template =
    t.trackingLastOpened ||
    (String(lang || "en").toLowerCase() === "fr" ? "Dernière ouverture {time}" : "Last opened {time}");

  return String(template || "").replace("{time}", rel || "");
}


function countStoryContributors(story) {
  const scenes = Array.isArray(story?.scenes) ? story.scenes : [];
  const names = scenes
    .map((scene) => String(scene?.authorName || scene?.authorUsername || scene?.author || scene?.createdBy || "").trim())
    .filter(Boolean);
  return Array.from(new Set(names)).length;
}

function storyLastActivityAt(story) {
  const scenes = Array.isArray(story?.scenes) ? story.scenes : [];
  const candidates = [story?.updatedAt, story?.lastUpdatedAt, story?.canonUpdatedAt, story?.createdAt];
  scenes.forEach((scene) => {
    candidates.push(scene?.updatedAt, scene?.createdAt, scene?.decisionAt);
  });
  let best = 0;
  candidates.forEach((value) => {
    const t = new Date(value || 0).getTime();
    if (Number.isFinite(t) && t > best) best = t;
  });
  return best ? new Date(best).toISOString() : "";
}

function storyPulseLabel(story, lang = "en") {
  const iso = storyLastActivityAt(story);
  const isFR = String(lang || "en").toLowerCase() === "fr";
  if (!iso) return isFR ? "Calme" : "Quiet";
  const diffHours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (diffHours < 24) return isFR ? "Actif maintenant" : "Active now";
  if (diffHours < 24 * 7) return isFR ? "Actif récemment" : "Recently active";
  return isFR ? "Calme" : "Quiet";
}

function storyDirectionLabel(story, lang = "en") {
  const isFR = String(lang || "en").toLowerCase() === "fr";
  const proposalsOpen = !!story?.proposalsOpen;
  const scenes = Array.isArray(story?.scenes) ? story.scenes : [];
  const pending = scenes.filter((s) => {
    const type = String(s?.type || "").toLowerCase();
    const status = String(s?.status || "").toLowerCase();
    return (type === "proposal" || type === "proposed") && (status === "pending" || !status);
  }).length;
  if (proposalsOpen && pending > 0) return isFR ? "Décision de l’auteur attendue" : "Awaiting author decision";
  if (proposalsOpen) return isFR ? "Propositions ouvertes" : "Proposal round open";
  return isFR ? "Canon en progression" : "Canon building";
}


function safeText(t) {
  return String(t || "").trim();
}



function safeStr(v) {
  return safeText(v);
}

function normalizeStoryVisibilityStatus(value, publicationStatusValue = "") {
  const raw = String(value || "").trim();
  const compact = raw.toLowerCase().replace(/[\s-]+/g, "_");
  const pub = String(publicationStatusValue || "").trim().toLowerCase();

  if (compact === "private" || compact === "private_draft" || compact === "hidden") return "private_draft";
  if (
    compact === "nextscenes_readers" ||
    compact === "nextscenes_readers_only" ||
    compact === "readers" ||
    compact === "readers_only" ||
    compact === "registered" ||
    compact === "logged_in" ||
    compact === "logged_in_readers"
  ) return "nextscenes_readers";
  if (
    pub === "publishedamazon" ||
    pub === "publishedexternal" ||
    pub === "published"
  ) {
    return "published";
  }

  if (
    compact === "published" ||
    compact === "published_completed" ||
    compact === "published_book"
  ) {
    return "public_preview";
  }
  if (compact === "public" || compact === "public_preview" || compact === "preview") return "public_preview";
  return "public_preview";
}

function getStoryVisibilityStatus(story) {
  return normalizeStoryVisibilityStatus(
    story?.visibilityStatus || story?.readerVisibility || story?.storyVisibility || story?.visibility,
    story?.publicationStatus || story?.publishingStatus
  );
}

function getReaderVisibilityCopy(status, lang = "en") {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const normalized = normalizeStoryVisibilityStatus(status);
  const copy = {
    private_draft: {
      en: { label: "Private Draft", note: "Only the author and admin can read this draft." },
      fr: { label: "Brouillon privé", note: "Seuls l’auteur et l’admin peuvent lire ce brouillon." },
    },
    nextscenes_readers: {
      en: { label: "Readers Only", note: "This story is available to logged-in NextScenes readers." },
      fr: { label: "Lecteurs seulement", note: "Cette histoire est disponible pour les lecteurs NextScenes connectés." },
    },
    public_preview: {
      en: { label: "Public Preview", note: "This story is still growing. Scenes may change before final publication." },
      fr: { label: "Aperçu public", note: "Cette histoire grandit encore. Les scènes peuvent changer avant la publication finale." },
    },
    published: {
      en: { label: "Published / Completed", note: "This is a completed or published work." },
      fr: { label: "Publié / terminé", note: "Ceci est une œuvre terminée ou publiée." },
    },
  };
  return copy[normalized]?.[isFR ? "fr" : "en"] || copy.public_preview[isFR ? "fr" : "en"];
}

function VisibilityStatusPill({ story, status, lang, style }) {
  const normalized = normalizeStoryVisibilityStatus(status || getStoryVisibilityStatus(story));
  const copy = getReaderVisibilityCopy(normalized, lang);
  return (
    <span
      className={`ns-visibility-chip is-${normalized.replace(/_/g, "-")}`}
      title={copy.note}
      style={style}
    >
      {copy.label}
    </span>
  );
}

function PublicationStatusPill({ info }) {
  if (!info?.isPublished) return null;
  return <span className="ns-publication-chip is-published">{info.publicationLabel}</span>;
}

function PreviewStatusPill({ info }) {
  if (!info?.isPublished) return null;
  return <span className="ns-publication-chip is-preview">{info.developmentLabel}</span>;
}

function getReaderPublicationInfo(story, lang) {
  return getKnownPublicationInfo(story, lang);
}

function getReaderPublishedAuthorName(story, publicationInfo) {
  return (
    safeText(publicationInfo?.publishedAuthorName) ||
    getPublishedAuthorName(story, guessAuthorName(story))
  );
}

function getReaderPublishedCardText(lang) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  return isFR
    ? "Livre publié. Informations finales et lien de publication disponibles sur NextScenes."
    : "Published book. Final book details and publication link available on NextScenes.";
}


function PublishedReaderNotice({ story, publicationInfo, lang }) {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  if (!publicationInfo?.isPublished) return null;

  return (
    <div className="ns-published-reader-notice" role="note">
      <div className="ns-publication-mini-row">
        <PublicationStatusPill info={publicationInfo} />
      </div>
      <h3>
        {isFR
          ? "Livre complété et publié"
          : "Completed and published book"}
      </h3>
      <p>
        {isFR
          ? "Cette page présente l’identité finale du livre, sa couverture, son synopsis et son lien de publication. Les anciennes scènes de développement ne sont plus affichées."
          : "This page presents the final book identity, cover, synopsis, and publication link. Earlier development scenes are no longer displayed."}
      </p>
      {publicationInfo.amazonHref ? (
        <button
          className="btn ns-amazon-action"
          type="button"
          onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
          style={{ minHeight: 38 }}
        >
          {publicationInfo.cta}
        </button>
      ) : null}
    </div>
  );
}

function getApprovedSceneIllustration(scene) {
  const status = safeText(scene?.illustrationStatus).toLowerCase();
  const url = safeText(scene?.illustrationUrl);
  if (status !== "approved" || !url) return null;
  return {
    url,
    caption: safeText(scene?.illustrationCaption),
    credit: safeText(scene?.illustrationCredit),
    alt: safeText(scene?.illustrationAltText || scene?.illustrationCaption || scene?.title || "Scene illustration"),
  };
}

function ReaderSceneIllustration({ scene, t }) {
  const art = getApprovedSceneIllustration(scene);
  if (!art) return null;
  const caption = safeText(art.caption);
  const credit = safeText(art.credit);
  return (
    <figure className="ns-scene-illustration-public ns-reader-illustration-public">
      <div className="ns-scene-illustration-public-frame">
        <img
          src={art.url}
          alt={art.alt || (t.sceneIllustrationLabel || "Scene illustration")}
          loading="lazy"
          onError={(e) => {
            try {
              e.currentTarget.style.display = "none";
            } catch {}
          }}
        />
      </div>
      {(caption || credit) ? (
        <figcaption className="ns-scene-illustration-public-caption">
          {caption ? <span className="ns-scene-illustration-public-title">{caption}</span> : null}
          {credit ? (
            <span className="ns-scene-illustration-public-credit">
              {t.sceneIllustrationCredit || "Credit"}: {credit}
            </span>
          ) : null}
        </figcaption>
      ) : null}
    </figure>
  );
}


function sceneRatingSceneId(scene) {
  return String(scene?._id || scene?.id || "").trim();
}

function sceneRatingStorageKey(storyId, sceneId) {
  const sid = String(storyId || "").trim();
  const scid = String(sceneId || "").trim();
  return sid && scid ? `ns.sceneRating.${sid}.${scid}` : "";
}

function readLocalSceneRating(storyId, sceneId) {
  const key = sceneRatingStorageKey(storyId, sceneId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
  } catch {
    return null;
  }
}

function writeLocalSceneRating(storyId, sceneId, rating) {
  const key = sceneRatingStorageKey(storyId, sceneId);
  const n = Number(rating);
  if (!key || !Number.isInteger(n) || n < 1 || n > 5 || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(n));
  } catch {}
}

function getOrCreateSceneRatingVisitorToken() {
  if (typeof window === "undefined") return "";

  try {
    const existing = String(window.localStorage.getItem("ns.sceneRatingVisitorToken") || "").trim();
    if (existing.length >= 16 && existing.length <= 160) return existing;

    let token = "";
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      token = window.crypto.randomUUID();
    } else if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      const arr = new Uint32Array(4);
      window.crypto.getRandomValues(arr);
      token = Array.from(arr).map((x) => x.toString(16)).join(":");
    } else {
      token = `${Date.now()}:${Math.random().toString(36).slice(2)}:${Math.random().toString(36).slice(2)}`;
    }

    window.localStorage.setItem("ns.sceneRatingVisitorToken", token);
    return token;
  } catch {
    return `${Date.now()}:${Math.random().toString(36).slice(2)}:${Math.random().toString(36).slice(2)}`;
  }
}

const SCENE_REACTION_CODES = Object.freeze([
  "want_more",
  "exciting",
  "emotional",
  "like_character",
  "confusing",
  "too_long",
  "afraid_for_character",
  "curious_ending",
]);

function normalizeSceneReactionCode(value) {
  const code = String(value || "").trim().toLowerCase();
  return SCENE_REACTION_CODES.includes(code) ? code : "";
}

function normalizeSceneReactionList(values) {
  const input = Array.isArray(values) ? values : [];
  return Array.from(
    new Set(
      input
        .map((value) => normalizeSceneReactionCode(value))
        .filter(Boolean)
    )
  );
}

function sceneReactionStorageKey(storyId, sceneId) {
  const sid = String(storyId || "").trim();
  const scid = String(sceneId || "").trim();
  return sid && scid ? `ns.sceneReactions.${sid}.${scid}` : "";
}

function readLocalSceneReactions(storyId, sceneId) {
  const key = sceneReactionStorageKey(storyId, sceneId);
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return normalizeSceneReactionList(parsed);
  } catch {
    return [];
  }
}

function writeLocalSceneReactions(storyId, sceneId, reactions) {
  const key = sceneReactionStorageKey(storyId, sceneId);
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(normalizeSceneReactionList(reactions)));
  } catch {}
}

function SceneReactionStrip({
  storyId,
  scene,
  summary,
  status,
  isAuthenticated,
  labels,
  onToggleReaction,
}) {
  const sceneId = sceneRatingSceneId(scene);
  if (!storyId || !sceneId) return null;

  const selectedReactions = normalizeSceneReactionList(summary?.myReactions);
  const isSaving = String(status || "") === "saving";
  const isSaved = String(status || "") === "saved";
  const isError = String(status || "") === "error";

  const message = isSaving
    ? labels.saving
    : isError
    ? labels.error
    : isSaved
    ? (isAuthenticated ? labels.saved : labels.guestSaved)
    : selectedReactions.length
    ? String(labels.current || "").replace("{count}", String(selectedReactions.length))
    : "";

  const reactionLabels = labels.reactions || {};

  return (
    <div className="ns-scene-reactions" aria-label={labels.prompt}>
      <span className="ns-scene-reactions-prompt">{labels.prompt}</span>
      <div className="ns-scene-reactions-options" role="group" aria-label={labels.prompt}>
        {SCENE_REACTION_CODES.map((code) => {
          const active = selectedReactions.includes(code);
          const label = reactionLabels[code] || code;
          return (
            <button
              key={code}
              type="button"
              className={`ns-scene-reaction-chip${active ? " is-active" : ""}`}
              onClick={() => onToggleReaction(scene, code)}
              disabled={isSaving}
              aria-pressed={active ? "true" : "false"}
              aria-label={String(labels.aria || "").replace("{reaction}", label)}
              title={String(labels.aria || "").replace("{reaction}", label)}
            >
              {label}
            </button>
          );
        })}
      </div>
      {message ? <span className="ns-scene-reactions-note">{message}</span> : null}
    </div>
  );
}


function pickFirstFiniteNumber(source, keys) {
  const obj = source && typeof source === "object" ? source : {};
  for (const key of keys) {
    const parts = String(key || "").split(".");
    let current = obj;
    for (const part of parts) {
      current = current && typeof current === "object" ? current[part] : undefined;
    }
    const n = Number(current);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeSceneRatingAverage(summary) {
  const n = pickFirstFiniteNumber(summary, [
    "overallAverage",
    "averageRating",
    "ratingAverage",
    "avgRating",
    "average",
    "avg",
    "meanRating",
    "ratings.average",
    "ratings.avg",
    "rating.avg",
  ]);

  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(5, n));
}

function normalizeSceneRatingCount(summary) {
  const n = pickFirstFiniteNumber(summary, [
    "overallCount",
    "ratingCount",
    "ratingsCount",
    "totalRatings",
    "totalRatingCount",
    "readerRatingCount",
    "publicRatingCount",
    "count",
    "total",
    "ratings.count",
    "rating.count",
  ]);

  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

function formatSceneRatingAverage(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n.toFixed(1).replace(/\.0$/, ".0");
}

function formatReaderRatingCount(count, labels) {
  const n = Number(count);
  const safe = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  const template = safe === 1 ? labels.summarySingular : labels.summaryPlural;
  return String(template || "{count} reader ratings").replace("{count}", String(safe));
}

function SceneRatingSummary({ summary, labels }) {
  const average = normalizeSceneRatingAverage(summary);
  const count = normalizeSceneRatingCount(summary);
  const hasPublicSummary = count > 0 && Number.isFinite(Number(average)) && Number(average) > 0;
  const rounded = hasPublicSummary ? Math.max(1, Math.min(5, Math.round(Number(average)))) : 0;
  const averageText = hasPublicSummary ? formatSceneRatingAverage(average) : "";

  return (
    <div className="ns-scene-rating-summary" aria-live="polite">
      <span className="ns-scene-rating-summary-label">
        {labels.summaryLabel}
      </span>

      {hasPublicSummary ? (
        <span
          className="ns-scene-rating-summary-body"
          aria-label={String(labels.summaryAria || "")
            .replace("{average}", averageText)
            .replace("{count}", String(count))}
        >
          <span className="ns-scene-rating-summary-stars" aria-hidden="true">
            {[1, 2, 3, 4, 5].map((value) => (
              <span
                key={value}
                className={`ns-scene-rating-summary-star${value <= rounded ? " is-active" : ""}`}
              >
                ★
              </span>
            ))}
          </span>
          <strong className="ns-scene-rating-summary-average">{averageText}</strong>
          <span className="ns-scene-rating-summary-count">
            {formatReaderRatingCount(count, labels)}
          </span>
        </span>
      ) : (
        <span className="ns-scene-rating-summary-empty">
          {labels.summaryEmpty}
        </span>
      )}
    </div>
  );
}

function SceneRatingStrip({
  storyId,
  scene,
  summary,
  status,
  isAuthenticated,
  labels,
  onRate,
}) {
  const sceneId = sceneRatingSceneId(scene);
  if (!storyId || !sceneId) return null;

  const rating = Number(summary?.myRating || 0) || 0;
  const isSaving = String(status || "") === "saving";
  const isSaved = String(status || "") === "saved";
  const isError = String(status || "") === "error";

  const message = isSaving
    ? labels.saving
    : isError
    ? labels.error
    : isSaved
    ? (isAuthenticated ? labels.saved : labels.guestSaved)
    : rating
    ? String(labels.current || "").replace("{rating}", String(rating))
    : "";

  return (
    <div className="ns-scene-rating" aria-label={labels.prompt}>
      <SceneRatingSummary summary={summary} labels={labels} />

      <div className="ns-scene-rating-action">
        <span className="ns-scene-rating-prompt">{labels.prompt}</span>
        <div className="ns-scene-rating-stars" role="group" aria-label={labels.prompt}>
          {[1, 2, 3, 4, 5].map((value) => {
            const active = value <= rating;
            return (
              <button
                key={value}
                type="button"
                className={`ns-scene-rating-star${active ? " is-active" : ""}`}
                onClick={() => onRate(scene, value)}
                disabled={isSaving}
                aria-label={String(labels.aria || "").replace("{rating}", String(value))}
                title={String(labels.aria || "").replace("{rating}", String(value))}
              >
                ★
              </button>
            );
          })}
        </div>
        {message ? <span className="ns-scene-rating-note">{message}</span> : null}
      </div>
    </div>
  );
}

function clampWords(text, maxWords) {
  const t = safeText(text);
  if (!t) return "";
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length <= maxWords) return t;
  return parts.slice(0, maxWords).join(" ") + "…";
}

function getId(story) {
  return story?._id || story?.id || null;
}

function getIdAliases(story) {
  const out = [];
  const a = String(story?._id || "").trim();
  const b = String(story?.id || "").trim();
  if (a) out.push(a);
  if (b && b !== a) out.push(b);
  return out;
}

function approvedCanonScenes(story) {
  const scenes = Array.isArray(story?.scenes) ? story.scenes : [];
  return scenes.filter((s) => s?.type === "canon" && s?.status === "approved");
}

function firstCanonScene(story) {
  const canon = approvedCanonScenes(story);
  const sorted = canon
    .slice()
    .sort((a, b) => {
      const ta = new Date(a?.createdAt || 0).getTime();
      const tb = new Date(b?.createdAt || 0).getTime();
      return ta - tb;
    });

  return sorted[0] || null;
}

function withCanonSceneNumbers(scenes) {
  const rows = Array.isArray(scenes) ? scenes : [];
  const total = rows.length;
  return rows.map((scene, index) => ({
    ...scene,
    __canonSceneNumber: index + 1,
    __canonSceneTotal: total,
  }));
}

function lastFiveValidatedScenes(story) {
  const canon = approvedCanonScenes(story);
  const sorted = canon
    .slice()
    .sort((a, b) => {
      const ta = new Date(a?.createdAt || 0).getTime();
      const tb = new Date(b?.createdAt || 0).getTime();
      return ta - tb;
    });

  const numbered = withCanonSceneNumbers(sorted);
  return numbered.slice(Math.max(0, numbered.length - 5));
}

function guessWritingMode(story) {
  return (
    safeText(story?.writingMode) ||
    safeText(story?.mode) ||
    safeText(story?.category) ||
    safeText(story?.genre) ||
    safeText(story?.forum) ||
    "General"
  );
}

function guessAuthorAbout(story) {
  return safeText(story?.aboutAuthor) || safeText(story?.authorBio) || safeText(story?.bio) || "";
}

function guessSynopsis(story) {
  return (
    safeText(story?.synopsis) ||
    safeText(story?.summary) ||
    safeText(story?.description) ||
    safeText(story?.logline) ||
    ""
  );
}

function guessDescription(story) {
  return safeText(
    story?.description ||
      story?.storyDescription ||
      story?.blurb ||
      story?.summary ||
      story?.meta?.description ||
      ""
  );
}

function guessAvatarUrl(story) {
  return safeText(
    story?.authorAvatarUrl ||
      story?.avatarUrl ||
      story?.author?.avatarUrl ||
      story?.author?.profile?.avatarUrl ||
      story?.authorProfile?.avatarUrl ||
      ""
  );
}

function initialsFromName(name) {
  const n = safeText(name);
  if (!n) return "?";
  const parts = n.split(" ").filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => p[0]?.toUpperCase()).filter(Boolean);
  return letters.join("") || n[0].toUpperCase();
}

function guessAuthorName(story) {
  const a =
    story?.authorName ||
    story?.author ||
    story?.createdByName ||
    story?.ownerName ||
    story?.writerName ||
    story?.userName ||
    "";
  const u =
    story?.authorUsername ||
    story?.createdByUsername ||
    story?.ownerUsername ||
    story?.writerUsername ||
    story?.username ||
    "";

  const name = safeText(a);
  const user = safeText(u);

  if (name && user && name.toLowerCase() !== user.toLowerCase()) return `${name} (@${user})`;
  if (name) return name;
  if (user) return `@${user}`;
  return "";
}

function parseReaderTargetFromUrl() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    const storyId = String(qs.get("storyId") || qs.get("id") || "").trim();
    const sceneId = String(qs.get("sceneId") || "").trim();
    const openRaw = String(qs.get("openValidated") || qs.get("open") || "").trim().toLowerCase();

    return {
      storyId,
      sceneId,
      openValidated: openRaw === "1" || openRaw === "true",
    };
  } catch {
    return { storyId: "", sceneId: "", openValidated: false };
  }
}

function sceneMatchesTargetId(scene, targetSceneId) {
  const target = String(targetSceneId || "").trim();
  if (!target) return false;

  return [scene?._id, scene?.id, scene?.localId, scene?.sceneId]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .includes(target);
}

function setStoryIdInUrl(storyId) {
  try {
    const id = String(storyId || "").trim();
    const base = "/reader-view";
    if (!id) {
      window.history.replaceState({}, "", base);
      return;
    }
    window.history.replaceState({}, "", `${base}?storyId=${encodeURIComponent(id)}`);
  } catch {}
}

/* -------------------- Mode badge -------------------- */

function normalizeMode(m) {
  const s = String(m || "").toLowerCase().trim();
  if (!s) return "collaborative";
  if (s.includes("solo")) return "solo";
  if (s.includes("group") && s.includes("closed")) return "group_closed";
  if (s.includes("group") && s.includes("open")) return "group_open";
  if (s.includes("collab")) return "collaborative";
  return "collaborative";
}

function modeMeta(mode) {
  const key = normalizeMode(mode);
  const base = {
    display: "Collaborative Writing",
    bg: "rgba(226, 248, 242, 0.92)",
    border: "rgba(16,185,129,0.42)",
    text: "rgba(6,95,70,0.95)",
    dot: "rgba(16,185,129,0.95)",
  };

  if (key === "solo") {
    return {
      ...base,
      display: "Solo Writing",
      bg: "rgba(246, 242, 234, 0.94)",
      border: "rgba(120, 113, 108, 0.38)",
      text: "rgba(68, 64, 60, 0.95)",
      dot: "rgba(87, 83, 78, 0.95)",
    };
  }

  if (key === "group_open") {
    return {
      ...base,
      display: "Group Writing (Open)",
      bg: "rgba(245, 244, 255, 0.92)",
      border: "rgba(99,102,241,0.32)",
      text: "rgba(49,46,129,0.95)",
      dot: "rgba(99,102,241,0.90)",
    };
  }

  if (key === "group_closed") {
    return {
      ...base,
      display: "Group Writing (Closed)",
      bg: "rgba(255, 247, 237, 0.94)",
      border: "rgba(245, 158, 11, 0.34)",
      text: "rgba(124, 45, 18, 0.95)",
      dot: "rgba(245, 158, 11, 0.90)",
    };
  }

  return base;
}

function ModePill({ mode, style, t }) {
  const m = modeMeta(mode);
  const displayMap = {
  "Solo Writing": t.soloWriting,
  "Collaborative Writing": t.collaborativeWriting,
  "Group Writing (Open)": t.groupWritingOpen,
  "Group Writing (Closed)": t.groupWritingClosed,
};
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: m.bg,
        border: `1px solid ${m.border}`,
        color: m.text,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.2px",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
        ...style,
      }}
      title={displayMap[m.display] || m.display}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: m.dot,
          boxShadow: "0 0 0 3px rgba(15, 23, 42, 0.04)",
        }}
      />
      {displayMap[m.display] || m.display}
    </span>
  );
}

/* -------------------- Shelf pill -------------------- */

function ShelfPill({ state, style, t }) {
  const unknown = state === "unknown";
  const on = state === "saved";

  const bg = unknown
    ? "rgba(241, 245, 249, 0.95)"
    : on
    ? "rgba(239, 246, 255, 0.95)"
    : "rgba(250, 250, 250, 0.95)";
  const border = unknown
    ? "rgba(100,116,139,0.22)"
    : on
    ? "rgba(59,130,246,0.28)"
    : "rgba(15, 23, 42, 0.10)";
  const text = unknown
    ? "rgba(71,85,105,0.95)"
    : on
    ? "rgba(30, 64, 175, 0.95)"
    : "rgba(51, 65, 85, 0.92)";
  const dot = unknown
    ? "rgba(100,116,139,0.85)"
    : on
    ? "rgba(59,130,246,0.92)"
    : "rgba(100,116,139,0.8)";
  const label = unknown
  ? t.checkingShelf
  : on
  ? t.onMyShelf
  : t.notOnShelf;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color: text,
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: "0.2px",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
        ...style,
      }}
      title={label}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: dot,
          boxShadow: "0 0 0 3px rgba(15, 23, 42, 0.04)",
        }}
      />
      {label}
    </span>
  );
}

/* -------------------- Search helpers -------------------- */

function normalizeForSearch(s) {
  return String(s || "").toLowerCase().trim();
}

function storyMatchesQuery(story, q) {
  const needle = normalizeForSearch(q);
  if (!needle) return true;

  const title = normalizeForSearch(story?.title);
  const publicationTitle = normalizeForSearch(story?.publicationTitle || story?.publishedTitle);
  const publicationSubtitle = normalizeForSearch(story?.publicationSubtitle || story?.publishedSubtitle);
  const forum = normalizeForSearch(story?.forum);
  const mode = normalizeForSearch(guessWritingMode(story));
  const author = normalizeForSearch(guessAuthorName(story));
  const desc = normalizeForSearch(guessDescription(story));

  const hay = `${title} ${publicationTitle} ${publicationSubtitle} ${forum} ${mode} ${author} ${desc}`;
  return hay.includes(needle);
}

function storyMatchesMode(story, modeFilter) {
  const f = normalizeForSearch(modeFilter);
  if (!f || f === "all") return true;

  const m = normalizeMode(guessWritingMode(story));
  return m === f;
}

/* -------------------- Tracking helpers -------------------- */

function bumpTrackingStamp() {
  try {
    window.localStorage.setItem("ns.tracking.changedAt", String(Date.now()));
  } catch {}
}

function setSelectedStoryForNav(storyId) {
  const id = String(storyId || "").trim();
  if (!id) return;
  try {
    window.localStorage.setItem("ns.selectedStoryId", id);
    window.localStorage.setItem("ns.reader.focusStoryId", id);
  } catch {}
}

async function fetchTrackingOne(storyId) {
  const id = String(storyId || "").trim();
  if (!id) return null;

  try {
    if (typeof api.getStoryTracking === "function") {
      const res = await api.getStoryTracking(id);
      if (res && typeof res === "object") return res;
    }
  } catch {}
  return null;
}

async function postTrackingOpen(storyId) {
  const id = String(storyId || "").trim();
  if (!id) return;

  try {
    if (typeof api.recordStoryTrackingView === "function") {
      await api.recordStoryTrackingView(id, { surface: "readerView" });
    }
  } catch {}
}

async function postTrackingProgress(storyId, payload) {
  const id = String(storyId || "").trim();
  if (!id) return;

  const body = payload && typeof payload === "object" ? payload : {};
  try {
    if (typeof api.markStoryTrackingSeen === "function") {
      await api.markStoryTrackingSeen(id, {
        surface: "readerView",
        markCanon: !!body.canonUpdatedAt,
      });
    }
  } catch {}
}

/* -------------------- Shelf helpers -------------------- */

function uniqueIdsFromStoriesWithAliases(arr) {
  const out = [];
  const seen = new Set();
  (Array.isArray(arr) ? arr : []).forEach((s) => {
    getIdAliases(s).forEach((id) => {
      const clean = String(id || "").trim();
      if (!clean) return;
      if (seen.has(clean)) return;
      seen.add(clean);
      out.push(clean);
    });
  });
  return out;
}

function buildAliasGroups(arr) {
  const map = {};
  (Array.isArray(arr) ? arr : []).forEach((s) => {
    const aliases = getIdAliases(s);
    if (aliases.length === 0) return;
    aliases.forEach((a) => {
      map[a] = aliases.slice();
    });
  });
  return map;
}

function normalizeShelfStatusResponse(data, ids) {
  const d = data || {};
  const base = {};

  const maybeMap = d.savedById || d.statuses || d.status || d.map;
  if (maybeMap && typeof maybeMap === "object" && !Array.isArray(maybeMap)) {
    ids.forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(maybeMap, id)) base[id] = Boolean(maybeMap[id]);
    });
  } else if (Array.isArray(d.items)) {
    d.items.forEach((it) => {
      const sid = String(it?.storyId || it?.id || it?.story || "").trim();
      if (!sid) return;
      base[sid] = Boolean(it?.saved);
    });
  } else if (d && typeof d === "object" && !Array.isArray(d)) {
    ids.forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(d, id)) base[id] = Boolean(d[id]);
    });
  }

  ids.forEach((id) => {
    if (!Object.prototype.hasOwnProperty.call(base, id)) base[id] = false;
  });

  return base;
}

async function fetchShelfStatuses(ids) {
  const clean = (Array.isArray(ids) ? ids : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  if (clean.length === 0) return {};

  const qs1 = new URLSearchParams();
  qs1.set("ids", clean.join(","));

  try {
    const data = await api(`/api/library/shelf/status?${qs1.toString()}`, { method: "GET" });
    return normalizeShelfStatusResponse(data, clean);
  } catch {
    const qs2 = new URLSearchParams();
    qs2.set("storyIds", clean.join(","));
    const data2 = await api(`/api/library/shelf/status?${qs2.toString()}`, { method: "GET" });
    return normalizeShelfStatusResponse(data2, clean);
  }
}

async function addToShelf(storyId) {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Missing story id.");

  const tries = [
    () => api.post(`/api/library/shelf/${encodeURIComponent(id)}/save`, {}),
    () => api.post(`/api/library/shelf/${encodeURIComponent(id)}/add`, {}),
    () => api.post(`/api/library/shelf/${encodeURIComponent(id)}`, {}),
    () => api.post(`/api/library/shelf/add`, { storyId: id }),
    () => api.post(`/api/library/shelf`, { storyId: id }),
  ];

  let lastErr = null;
  for (const fn of tries) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Shelf add failed.");
}

async function getFollowStatus(storyId) {
  const id = String(storyId || "").trim();
  if (!id) return false;

  const data = await api(
    `/api/library/follow/${encodeURIComponent(id)}/status`,
    { method: "GET" }
  );

  return Boolean(data?.following);
}

async function followStory(storyId) {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Missing story id.");

  return api.post(`/api/library/follow/${encodeURIComponent(id)}`, {});
}

async function unfollowStory(storyId) {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Missing story id.");

  return api.del(`/api/library/follow/${encodeURIComponent(id)}`);
}

async function removeFromShelf(storyId) {
  const id = String(storyId || "").trim();
  if (!id) throw new Error("Missing story id.");

  const tries = [
    () => api.post(`/api/library/shelf/${encodeURIComponent(id)}/remove`, {}),
    () => api.post(`/api/library/shelf/${encodeURIComponent(id)}/unsave`, {}),
    () => api.del(`/api/library/shelf/${encodeURIComponent(id)}`),
    () => api.post(`/api/library/shelf/remove`, { storyId: id }),
  ];

  let lastErr = null;
  for (const fn of tries) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Shelf remove failed.");
}

/* =========================
   Step 5: Skeleton UI
   ========================= */

function Skel({ w = "100%", h = 12, r = 10, style }) {
  return <div className="ns-skel" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

function SkeletonListItem() {
  return (
    <div className="ns-card ns-card-soft" style={{ padding: 12, marginBottom: 10 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div className="ns-skel" style={{ width: 28, height: 28, borderRadius: 999, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <Skel w="72%" h={12} />
          <div style={{ marginTop: 8 }}>
            <Skel w="55%" h={11} />
          </div>
        </div>
      </div>
      <div style={{ marginTop: 14, paddingTop: 8, borderTop: "1px solid rgba(15, 23, 42, 0.08)" }}>
        <Skel w="88%" h={11} />
      </div>
    </div>
  );
}

function SkeletonRightPanel() {
  return (
    <div className="ns-card ns-card-soft" style={{ padding: 14 }}>
      <Skel w="62%" h={18} r={12} />
      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Skel w="160px" h={30} r={999} />
        <Skel w="140px" h={30} r={999} />
        <Skel w="160px" h={30} r={999} />
      </div>

      <div style={{ marginTop: 14 }}>
        <Skel w="92%" h={12} />
        <div style={{ marginTop: 10 }}>
          <Skel w="88%" h={12} />
        </div>
        <div style={{ marginTop: 10 }}>
          <Skel w="84%" h={12} />
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div className="ns-skel" style={{ height: 120, borderRadius: 16 }} />
      </div>
    </div>
  );
}

export default function ReaderView({
  onNavigatePage,
  projectHubContext = {},
}) {
  const { isAuthenticated, user } = useUser();

  const lang =
    (typeof window !== "undefined" && window.localStorage.getItem("ns.lang")) || "en";
  const t = TEXT[lang] || TEXT.en;
  const isFR = String(lang || "en").toLowerCase() === "fr";

  const translateWritingMode = (mode) => {
  const m = String(mode || "").trim().toLowerCase();

  if (m === "solo") return t.solo;
  if (m === "collaborative") return t.collaborative;
  if (m === "group open" || m === "group_open" || m === "group (open)") return t.groupOpen;
  if (m === "group closed" || m === "group_closed" || m === "group (closed)") return t.groupClosed;

  return mode || t.general;
};


  const uiText = useMemo(() => {
    const isFR = String(lang || "en").toLowerCase() === "fr";
    return {
      shelfSyncing: t.shelfSyncing || (isFR ? "Étagère en synchronisation…" : "Shelf syncing…"),
      shelfReady: t.shelfReady || (isFR ? "Étagère prête" : "Shelf ready"),
      loadMoreStorylines: t.loadMoreStorylines || (isFR ? "Charger plus de storylines" : "Load More Storylines"),
      guestsCanRead:
        t.guestsCanReadReaderView ||
        (isFR
          ? "Les visiteurs peuvent lire gratuitement. L’inscription est gratuite pour utiliser l’Étagère, proposer des scènes, voter et rejoindre les discussions."
          : "Guests can read for free. Registration is free to use Shelf, propose scenes, vote, and join discussions."),
      shelfLibraryNotFeed:
        t.shelfLibraryNotFeed ||
        (isFR
          ? "Bienvenue{NAME}. Votre Étagère est votre bibliothèque, pas un fil social."
          : "Welcome{NAME}. Your Shelf is your library, not a social feed."),
      welcomeToReaderView: t.welcomeToReaderView || (isFR ? "Bienvenue dans Reader View" : "Welcome to Reader View"),
      unfoldOneScene:
        t.readerOrientationIntro ||
        (isFR
          ? "Les histoires se déploient une scène à la fois. Choisissez une storyline à gauche et entrez doucement dans le récit."
          : "Stories unfold one scene at a time. Choose a storyline on the left and step quietly into the narrative."),
      returnLater:
        t.readerOrientationReturn ||
        (isFR
          ? "D’ici, vous pouvez lire les scènes validées, enregistrer des histoires sur votre Étagère et revenir plus tard sans perdre votre place."
          : "From here you can read validated scenes, save stories to your Shelf, and return later without losing your place."),
      storyHubStart:
        t.storyHubStartNew ||
        (isFR ? "Story Hub → Commencer une nouvelle storyline" : "Story Hub → Start a new storyline"),
      writerStudioContinue:
        t.writerStudioContinue ||
        (isFR ? "Writer Studio → Continuer à écrire" : "Writer Studio → Continue writing"),
      writerStudioWriteSubmit:
        t.writerStudioWriteSubmit ||
        (isFR ? "Writer Studio → Écrire ou soumettre des propositions" : "Writer Studio → Write or submit proposals"),
      selectStoryToBegin:
        t.selectStoryToBegin || (isFR ? "Sélectionnez une storyline pour commencer." : "Select a storyline to begin."),
      working: t.working || (isFR ? "Traitement…" : "Working…"),
      syncing: t.syncing || (isFR ? "synchronisation…" : "syncing…"),
      notAvailable: t.notAvailable || (isFR ? "Non disponible" : "Not available"),
      safeParticipation:
        t.readerSafeParticipation ||
        (isFR
          ? "Vous ne pouvez pas abîmer l’histoire. Les contributions passent par le flux normal de révision et de validation."
          : "You can’t break the story. Contributions go through the normal review and validation flow."),
      continueWriting:
        t.readerContinueWriting ||
        (isFR ? "Écrire ce qui se passe ensuite" : "Write What Happens Next"),
      suggestNextScene:
        t.readerSuggestNextScene ||
        (isFR ? "Suggérer la scène suivante" : "Suggest the Next Scene"),
      failedToLoadSelectedStory:
        t.failedToLoadSelectedStory ||
        (isFR ? "Impossible de charger la storyline sélectionnée." : "Failed to load selected story."),
      failedToLoadStorylines:
        t.failedToLoadStorylines ||
        (isFR ? "Impossible de charger les storylines." : "Failed to load storylines."),
      shelfActionFailed:
        t.shelfActionFailed ||
        (isFR ? "Échec de l’action sur l’Étagère." : "Shelf action failed."),
      shelfRouteMismatch:
        t.shelfRouteMismatch ||
        (isFR
          ? "Échec de l’action sur l’Étagère (route introuvable). Le nom de route du backend ne correspond pas à celui appelé par Reader View."
          : "Shelf action failed (route not found). Your backend add route name does not match what ReaderView is calling."),
      untitledStoryline: t.untitledStoryline || (isFR ? "Storyline sans titre" : "Untitled Storyline"),
      unclassifiedForum: t.unclassifiedForum || (isFR ? "Forum non classé" : "Unclassified Forum"),
      unknownAuthor: t.unknown || (isFR ? "Inconnu" : "Unknown"),
      storylineLabel: t.storyline || (isFR ? "Storyline" : "Storyline"),
      loadingFullStory:
        t.loadingFullStory || (isFR ? "Chargement de l’histoire complète…" : "Loading full story…"),
      openStorylineHint:
        t.openStorylineHint || (isFR ? "Ouvrir cette storyline" : "Open this storyline"),
      openLastFiveHint:
        t.openLastFiveHint ||
        (isFR ? "Ouvrir et afficher les cinq dernières scènes validées" : "Open and view the last five validated scenes"),
      showMoreStorylinesTitle:
        t.showMoreStorylinesTitle || (isFR ? "Afficher plus de storylines" : "Show more storylines"),
      loginToUseShelfTitle:
        t.loginToUseShelfTitle || (isFR ? "Inscription gratuite : connectez-vous pour enregistrer des storylines dans votre Étagère." : "Free registration: log in to save storylines to your Shelf."),
      selectStoryFirst:
        t.selectStoryFirst || (isFR ? "Sélectionnez d’abord une storyline." : "Select a storyline first."),
      shelfStatusLoading:
        t.shelfStatusLoading || (isFR ? "Le statut de l’Étagère se charge encore." : "Shelf status is still loading."),
      removeFromShelfTitle:
        t.removeFromShelfTitle || (isFR ? "Retirer cette storyline de votre Étagère." : "Remove this storyline from your Shelf."),
      saveToShelfTitle:
        t.saveToShelfTitle || (isFR ? "Enregistrer cette storyline dans votre Étagère." : "Save this storyline to your Shelf."),
      openingSceneTitle:
        t.openingSceneTitle || (isFR ? "Scène d’ouverture" : "Opening Scene"),
      openingSceneContext:
        t.openingSceneContext || (isFR ? "Vous lisez ici le début de cette histoire." : "You are reading how this story began."),
      lastFiveContext:
        t.lastFiveContext ||
        (isFR
          ? "Vous lisez ici la partie récente validée de cette histoire. Seules les cinq dernières scènes validées sont affichées."
          : "You are reading the most recent validated part of this story. Only the last five validated scenes are shown here."),
      noOpeningScene:
        t.noOpeningScene || (isFR ? "Pas encore de scène d’ouverture." : "No opening scene yet."),
      previous: t.previous || (isFR ? "Précédent" : "Previous"),
      next: t.next || (isFR ? "Suivant" : "Next"),
      fromLabel: t.fromLabel || (isFR ? "De" : "From"),
      selectedStoryLabel: t.selectedStoryLabel || (isFR ? "Storyline sélectionnée" : "Selected storyline"),
      recentValidatedScenesLabel:
        t.recentValidatedScenesLabel || (isFR ? "Scènes validées récentes" : "Recent validated scenes"),
      sceneLabel: t.sceneLabel || (isFR ? "Scène" : "Scene"),
      openingSceneMeta:
        t.openingSceneMeta || (isFR ? "Scène d’ouverture" : "Opening Scene"),
      validatedReadingWindow:
        t.validatedReadingWindow ||
        (isFR
          ? "Fenêtre de lecture validée : scène d’ouverture et cinq dernières scènes validées seulement."
          : "Validated reading window: opening scene and the last five validated scenes only."),
      trackingLastOpenedLabel:
        t.trackingLastOpenedLabel || (isFR ? "Continuité de lecture" : "Reading continuity"),
      trackingLastOpened:
        t.trackingLastOpened || (isFR ? "Dernière ouverture {time}" : "Last opened {time}"),
      trackingLastOpenedHelper:
        t.trackingLastOpenedHelper ||
        (isFR
          ? "NextScenes garde en mémoire votre dernier passage dans cette storyline."
          : "NextScenes remembers your last visit to this storyline."),
      trackingNotOpenedYet:
        t.trackingNotOpenedYet || (isFR ? "Pas encore ouvert" : "Not opened yet"),
      trackingLoading:
        t.trackingLoading || (isFR ? "Mise à jour de la continuité…" : "Updating continuity…"),
      sceneRatingPrompt:
        t.sceneRatingPrompt || (isFR ? "Votre avis" : "Your rating"),
      sceneRatingSaved:
        t.sceneRatingSaved || (isFR ? "Enregistré." : "Saved."),
      sceneRatingGuestSaved:
        t.sceneRatingGuestSaved || (isFR ? "Enregistré comme invité." : "Saved as guest."),
      sceneRatingSaving:
        t.sceneRatingSaving || (isFR ? "Enregistrement…" : "Saving…"),
      sceneRatingError:
        t.sceneRatingError || (isFR ? "Non enregistré." : "Not saved."),
      sceneRatingAria:
        t.sceneRatingAria || (isFR ? "Noter cette scène {rating} sur 5" : "Rate this scene {rating} out of 5"),
      sceneRatingCurrent:
        t.sceneRatingCurrent || (isFR ? "Votre note : {rating}/5" : "Your rating: {rating}/5"),
      sceneRatingSummaryLabel:
        t.sceneRatingSummaryLabel || (isFR ? "Réponse des lecteurs" : "Reader response"),
      sceneRatingSummaryEmpty:
        t.sceneRatingSummaryEmpty || (isFR ? "Soyez parmi les premiers à noter cette scène." : "Be among the first to rate this scene."),
      sceneRatingSummarySingular:
        t.sceneRatingSummarySingular || (isFR ? "{count} note de lecteur" : "{count} reader rating"),
      sceneRatingSummaryPlural:
        t.sceneRatingSummaryPlural || (isFR ? "{count} notes de lecteurs" : "{count} reader ratings"),
      sceneRatingSummaryAria:
        t.sceneRatingSummaryAria || (isFR ? "Note moyenne {average} sur 5, basée sur {count} notes" : "Average rating {average} out of 5, based on {count} ratings"),
      sceneReactionPrompt:
        t.sceneReactionPrompt || (isFR ? "Qu’avez-vous ressenti ?" : "What did you feel?"),
      sceneReactionSaved:
        t.sceneReactionSaved || (isFR ? "Réaction enregistrée." : "Reaction saved."),
      sceneReactionGuestSaved:
        t.sceneReactionGuestSaved || (isFR ? "Réaction enregistrée comme invité." : "Reaction saved as guest."),
      sceneReactionSaving:
        t.sceneReactionSaving || (isFR ? "Enregistrement de la réaction…" : "Saving reaction…"),
      sceneReactionError:
        t.sceneReactionError || (isFR ? "Réaction non enregistrée." : "Reaction not saved."),
      sceneReactionAria:
        t.sceneReactionAria || (isFR ? "Choisir la réaction : {reaction}" : "Choose reaction: {reaction}"),
      sceneReactionCurrent:
        t.sceneReactionCurrent || (isFR ? "{count} réaction sélectionnée." : "{count} reaction selected."),
      sceneReactionLabels:
        t.sceneReactionLabels ||
        (isFR
          ? {
              want_more: "Je veux la suite",
              exciting: "Passionnant",
              emotional: "Émouvant",
              like_character: "J’aime ce personnage",
              confusing: "Confus",
              too_long: "Trop long",
              afraid_for_character: "J’ai peur pour ce personnage",
              curious_ending: "Fin intrigante",
            }
          : {
              want_more: "I want more",
              exciting: "Exciting",
              emotional: "Emotional",
              like_character: "I like this character",
              confusing: "Confusing",
              too_long: "Too long",
              afraid_for_character: "I fear for this character",
              curious_ending: "Curious ending",
            }),
      readerCopyProtectionNotice:
        t.readerCopyProtectionNotice ||
        (isFR ? "Lecture protégée : copie désactivée." : "Protected reading: copying is disabled."),
    };
  }, [lang, t]);

  function getParticipationGuidance(storyLike) {
    const isFR = String(lang || "en").toLowerCase() === "fr";
    const mode = normalizeMode(guessWritingMode(storyLike));

    if (mode === "solo") {
      return {
        title: t.readerParticipationTitle || (isFR ? "Comment participer" : "How participation works"),
        body:
          t.readerParticipationSolo ||
          (isFR
            ? "Ceci est une storyline solo. Seul l’auteur peut prolonger directement l’histoire canonique dans Writer Studio."
            : "This is a solo storyline. Only the author can continue the canon story directly in Writer Studio."),
        action: uiText.continueWriting,
      };
    }

    if (mode === "group_closed") {
      return {
        title: t.readerParticipationTitle || (isFR ? "Comment participer" : "How participation works"),
        body:
          t.readerParticipationGroupClosed ||
          (isFR
            ? "Ceci est une storyline de groupe fermé. La participation dépend de l’accès du groupe et de l’état actuel de l’histoire dans Writer Studio."
            : "This is a closed group storyline. Participation depends on group access and the story’s current state in Writer Studio."),
        action: uiText.suggestNextScene,
      };
    }

    return {
      title: t.readerParticipationTitle || (isFR ? "Comment participer" : "How participation works"),
      body:
        t.readerParticipationGeneral ||
        (isFR
          ? "Dans Writer Studio, vous pouvez prolonger l’histoire canonique ou soumettre une proposition selon l’état actuel de l’histoire et votre accès."
          : "In Writer Studio, you may continue the canon story or submit a proposal depending on the story’s current state and your access."),
      action: uiText.suggestNextScene,
    };
  }

  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingStory, setLoadingStory] = useState(false);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("last5");
  const [modalSceneIndex, setModalSceneIndex] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [visibleListCount, setVisibleListCount] = useState(READER_LIST_PAGE_SIZE);

  const mountedRef = useRef(true);
  const storyCacheRef = useRef(new Map());
  const storyReqSeqRef = useRef(0);
  const sceneProgressSentRef = useRef(new Set());
  const modalScrollRef = useRef(null);
  const sceneBottomRef = useRef(null);

  const initialTargetRef = useRef("");
  const initialSceneTargetRef = useRef("");
  const initialOpenValidatedRef = useRef(false);
  const didConsumeReaderIntentRef = useRef(false);
  const didAutoOpenRef = useRef(false);

  const listScrollRef = useRef(null);
  const selectedItemRef = useRef(null);
  const didAutoScrollRef = useRef(false);

  const [shelfMap, setShelfMap] = useState({});
  const [shelfBusyId, setShelfBusyId] = useState("");
  const [shelfError, setShelfError] = useState("");
  const [shelfHydrating, setShelfHydrating] = useState(false);

  const [followingSelected, setFollowingSelected] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [followError, setFollowError] = useState("");

  const aliasGroupsRef = useRef({});

  // Tracking: continue reading + updated since last read
  const [trackingSummary, setTrackingSummary] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [readerViewTab, setReaderViewTab] = useState("reading");

  const [sceneRatingSummaries, setSceneRatingSummaries] = useState({});
  const [sceneRatingStatus, setSceneRatingStatus] = useState({});
  const [sceneReactionSummaries, setSceneReactionSummaries] = useState({});
  const [sceneReactionStatus, setSceneReactionStatus] = useState({});

  useEffect(() => {
    if (!modalOpen || typeof document === "undefined" || typeof window === "undefined") return undefined;

    const isInsideProtectedText = (node) => {
      if (!node) return false;
      const element = node.nodeType === 1 ? node : node.parentElement;
      return !!(element && typeof element.closest === "function" && element.closest(".ns-reader-copyProtected"));
    };

    const hasProtectedSelection = () => {
      const selection = window.getSelection ? window.getSelection() : null;
      if (!selection || selection.rangeCount === 0 || String(selection.toString() || "").trim() === "") return false;
      return isInsideProtectedText(selection.anchorNode) || isInsideProtectedText(selection.focusNode);
    };

    const shouldBlockPointerEvent = (event) => isInsideProtectedText(event?.target);
    const shouldBlockClipboardEvent = (event) => shouldBlockPointerEvent(event) || hasProtectedSelection();

    const blockPointerEvent = (event) => {
      if (!shouldBlockPointerEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const blockClipboardEvent = (event) => {
      if (!shouldBlockClipboardEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const blockKeyboardCopy = (event) => {
      const key = String(event?.key || "").toLowerCase();
      const copyOrCut = (event?.metaKey || event?.ctrlKey) && (key === "c" || key === "x");
      if (!copyOrCut || !hasProtectedSelection()) return;
      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener("copy", blockClipboardEvent, true);
    document.addEventListener("cut", blockClipboardEvent, true);
    document.addEventListener("contextmenu", blockPointerEvent, true);
    document.addEventListener("dragstart", blockPointerEvent, true);
    document.addEventListener("selectstart", blockPointerEvent, true);
    document.addEventListener("keydown", blockKeyboardCopy, true);

    return () => {
      document.removeEventListener("copy", blockClipboardEvent, true);
      document.removeEventListener("cut", blockClipboardEvent, true);
      document.removeEventListener("contextmenu", blockPointerEvent, true);
      document.removeEventListener("dragstart", blockPointerEvent, true);
      document.removeEventListener("selectstart", blockPointerEvent, true);
      document.removeEventListener("keydown", blockKeyboardCopy, true);
    };
  }, [modalOpen]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearchQuery(String(searchInput || "").trim());
    }, 140);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setVisibleListCount(READER_LIST_PAGE_SIZE);
  }, [searchQuery, modeFilter]);


  useEffect(() => {
    const target = parseReaderTargetFromUrl();
    initialTargetRef.current = target.storyId;
    initialSceneTargetRef.current = target.sceneId;
    initialOpenValidatedRef.current = target.openValidated;

    try {
      const p = String(window.location.pathname || "").toLowerCase();
      const q = String(window.location.search || "");
      if (p === "/readerview") window.history.replaceState({}, "", `/reader-view${q}`);
    } catch {}
  }, []);

  const selectedId = getId(selectedStory);

  const selectedAliases = useMemo(
    () => (selectedStory ? getIdAliases(selectedStory) : selectedId ? [String(selectedId)] : []),
    [selectedStory, selectedId]
  );

  const selectedShelfState = useMemo(() => {
    if (!isAuthenticated) return "not_saved";
    if (!selectedAliases || selectedAliases.length === 0) return "unknown";

    for (const a of selectedAliases) {
      if (shelfMap[a] === true) return "saved";
      if (shelfMap[a] === false) return "not_saved";
    }
    return "unknown";
  }, [isAuthenticated, selectedAliases, shelfMap]);

  const lastOpenedStoryId = useMemo(() => {
    try {
      return String(localStorage.getItem("ns.reader.lastStoryId") || localStorage.getItem("ns.selectedStoryId") || "").trim();
    } catch {
      return "";
    }
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) {
      try {
        localStorage.setItem("ns.reader.lastStoryId", String(selectedId));
      } catch {}
    }
  }, [selectedId]);

  useEffect(() => {
    didAutoScrollRef.current = false;
  }, [selectedId]);

  useEffect(() => {
    setReaderViewTab("reading");
  }, [selectedId]);

  useEffect(() => {
    sceneProgressSentRef.current = new Set();
  }, [selectedId]);


  function putStoryInCache(storyLike, isFull = false) {
    const key = buildStoryCacheKey(storyLike);
    if (!key) return;
    const current = storyCacheRef.current.get(key) || {};
    const next = { ...current, ...(storyLike || {}) };
    if (isFull) next.__full = true;
    storyCacheRef.current.set(key, next);

    const aliases = getIdAliases(storyLike);
    aliases.forEach((alias) => {
      if (alias && alias !== key) storyCacheRef.current.set(String(alias), next);
    });

    if (storyCacheRef.current.size > STORY_CACHE_LIMIT) {
      const firstKey = storyCacheRef.current.keys().next().value;
      if (firstKey) storyCacheRef.current.delete(firstKey);
    }
  }

  function getStoryFromCache(id) {
    const key = String(id || "").trim();
    if (!key) return null;
    return storyCacheRef.current.get(key) || null;
  }

  useEffect(() => {
    if (!selectedId || !selectedStory || loadingStory) return;
    if (didConsumeReaderIntentRef.current) return;

    let wantsAuto = initialOpenValidatedRef.current === true;
    let targetSceneId = String(initialSceneTargetRef.current || "").trim();

    const routeStoryId = String(initialTargetRef.current || "").trim();
    if (routeStoryId) {
      const aliases = getIdAliases(selectedStory).map((value) => String(value || "").trim());
      if (!aliases.includes(routeStoryId)) return;
    }

    try {
      const flag = String(window.localStorage.getItem("ns.reader.autoOpenValidated") || "").trim();
      const sid = String(window.localStorage.getItem("ns.reader.autoOpenStoryId") || "").trim();
      const storedSceneId = String(window.localStorage.getItem("ns.reader.autoOpenSceneId") || "").trim();

      if (flag === "1" && sid && getIdAliases(selectedStory).map(String).includes(String(sid))) {
        wantsAuto = true;
        if (!targetSceneId) targetSceneId = storedSceneId;
        window.localStorage.removeItem("ns.reader.autoOpenValidated");
        window.localStorage.removeItem("ns.reader.autoOpenStoryId");
        window.localStorage.removeItem("ns.reader.autoOpenSceneId");
      }
    } catch {}

    if (!wantsAuto) return;

    const scenes = lastFiveValidatedScenes(selectedStory);
    if (!scenes.length) return;

    const matchingIndex = targetSceneId
      ? scenes.findIndex((scene) => sceneMatchesTargetId(scene, targetSceneId))
      : -1;
    const targetIndex = matchingIndex >= 0 ? matchingIndex : scenes.length - 1;

    didConsumeReaderIntentRef.current = true;
    setModalMode("last5");
    setModalSceneIndex(targetIndex);
    setModalOpen(true);
  }, [selectedId, selectedStory, loadingStory]);

  function mirrorShelfValueToAliases(alias, value) {
    const groups = aliasGroupsRef.current || {};
    const group = groups[String(alias)] || [String(alias)];
    setShelfMap((prev) => {
      const next = { ...(prev || {}) };
      group.forEach((k) => {
        next[String(k)] = Boolean(value);
      });
      return next;
    });
  }

  function forceMarkIdsNotSaved(arr) {
    const ids = uniqueIdsFromStoriesWithAliases(arr);
    if (!ids.length) return;

    setShelfMap((prev) => {
      const next = { ...(prev || {}) };
      ids.forEach((id) => {
        if (!Object.prototype.hasOwnProperty.call(next, id)) next[id] = false;
      });
      return next;
    });
  }

  async function hydrateShelfForList(arr) {
    if (!isAuthenticated) {
      setShelfMap({});
      aliasGroupsRef.current = {};
      return;
    }

    aliasGroupsRef.current = buildAliasGroups(arr);
    const ids = uniqueIdsFromStoriesWithAliases(arr);
    if (ids.length === 0) return;

    try {
      setShelfHydrating(true);
      setShelfError("");

      const statuses = await fetchShelfStatuses(ids);
      if (!mountedRef.current) return;

      const groups = aliasGroupsRef.current || {};
      const next = {};

      Object.keys(statuses || {}).forEach((k) => {
        const v = Boolean(statuses[k]);
        const group = groups[k] || [k];
        group.forEach((g) => {
          next[String(g)] = v;
        });
      });

      setShelfMap((prev) => ({ ...(prev || {}), ...(next || {}) }));
    } catch {
      if (!mountedRef.current) return;
      setShelfError(TEXT[(typeof window !== "undefined" && window.localStorage.getItem("ns.lang")) || "en"]?.shelfStatusFailed || TEXT.en.shelfStatusFailed);
      forceMarkIdsNotSaved(arr);
    } finally {
      if (mountedRef.current) setShelfHydrating(false);
    }
  }

  async function hydrateShelfForOneStory(fullStory) {
    if (!isAuthenticated) return;
    const aliases = getIdAliases(fullStory);
    if (aliases.length === 0) return;

    try {
      setShelfHydrating(true);
      const statuses = await fetchShelfStatuses(aliases);
      if (!mountedRef.current) return;

      Object.keys(statuses || {}).forEach((k) => {
        mirrorShelfValueToAliases(k, statuses[k]);
      });
    } catch {
      aliases.forEach((a) => mirrorShelfValueToAliases(a, false));
    } finally {
      if (mountedRef.current) setShelfHydrating(false);
    }
  }

  async function openStory(id, opts = {}) {
    const cleanId = String(id || "").trim();
    if (!cleanId) return;

    const deliberate = opts && opts.deliberate === true;
    const summaryMatch =
      stories.find((s) => getIdAliases(s).some((alias) => String(alias) === cleanId)) || null;
    const cached = getStoryFromCache(cleanId);
    const requestSeq = storyReqSeqRef.current + 1;
    storyReqSeqRef.current = requestSeq;

    if (summaryMatch) {
      putStoryInCache(summaryMatch, false);
      setSelectedStory((prev) => {
        if (prev && getIdAliases(prev).includes(cleanId) && Array.isArray(prev?.scenes) && prev.scenes.length) {
          return prev;
        }
        return summaryMatch;
      });
    } else if (cached) {
      setSelectedStory(cached);
    }

    const finalIdFromKnown = String(getId(summaryMatch) || getId(cached) || cleanId).trim();
    if (finalIdFromKnown) setStoryIdInUrl(finalIdFromKnown);

    const cachedFull = cached && cached.__full ? cached : null;
    if (cachedFull && !opts.force) {
      setSelectedStory(cachedFull);
      if (deliberate) {
        setSelectedStoryForNav(finalIdFromKnown || cleanId);
        bumpTrackingStamp();
      }

      if (isAuthenticated) {
        const trackId = finalIdFromKnown || cleanId;
        try {
          setTrackingLoading(true);
          await postTrackingOpen(trackId);
          const t = await fetchTrackingOne(trackId);
          if (mountedRef.current && storyReqSeqRef.current === requestSeq) setTrackingSummary(t);
          bumpTrackingStamp();
        } finally {
          if (mountedRef.current && storyReqSeqRef.current === requestSeq) setTrackingLoading(false);
        }

        hydrateShelfForOneStory(cachedFull);
      }
      return cachedFull;
    }

    try {
      setLoadingStory(true);
      setError("");
      const full =
        typeof api.getStoryById === "function"
          ? await api.getStoryById(cleanId)
          : await api(`/api/stories/${cleanId}`);

      if (!mountedRef.current) return;
      if (storyReqSeqRef.current !== requestSeq) return;

      const normalizedFull = full || null;
      if (normalizedFull) {
        putStoryInCache(normalizedFull, true);
      }
      setSelectedStory(normalizedFull);

      const finalId = getId(normalizedFull) || finalIdFromKnown || cleanId;
      setStoryIdInUrl(finalId);

      if (deliberate) {
        setSelectedStoryForNav(finalId);
        bumpTrackingStamp();
      }

      if (isAuthenticated) {
        // A fresh authorized story is required; tracking is not a reading gate.
        // Other openStory callers retain their original awaited behavior.
        const refreshTracking = async () => {
          try {
            setTrackingLoading(true);
            await postTrackingOpen(finalId);
            const t = await fetchTrackingOne(finalId);
            if (mountedRef.current && storyReqSeqRef.current === requestSeq) {
              setTrackingSummary(t);
              bumpTrackingStamp();
            }
          } finally {
            if (mountedRef.current && storyReqSeqRef.current === requestSeq) {
              setTrackingLoading(false);
            }
          }
        };
        if (opts.nonBlockingTracking === true) {
          // A tracking failure must not delay approved reading.
          void refreshTracking().catch(() => {});
        } else {
          await refreshTracking();
        }
      } else {
        if (mountedRef.current && storyReqSeqRef.current === requestSeq) {
          setTrackingSummary(null);
          setTrackingLoading(false);
        }
      }

      if (normalizedFull && isAuthenticated) {
        hydrateShelfForOneStory(normalizedFull);
      }
      return normalizedFull;
    } catch {
      if (!mountedRef.current) return;
      if (storyReqSeqRef.current !== requestSeq) return;
      setError(uiText.failedToLoadSelectedStory);
      if (!summaryMatch && !cached) setSelectedStory(null);
    } finally {
      if (!mountedRef.current) return;
      if (storyReqSeqRef.current === requestSeq) setLoadingStory(false);
    }
  }

  async function openStoryAndMaybeModal(id, wantsModal) {
    const cleanId = String(id || "").trim();
    if (!cleanId) return;

    const freshStory = await openStory(cleanId, {
      deliberate: true,
      force: true,
      nonBlockingTracking: wantsModal === true,
    });
    if (!mountedRef.current) return;

    if (wantsModal === true && freshStory && !getReaderPublicationInfo(freshStory, lang)?.isPublished) {
      setModalMode("last5");
      setModalSceneIndex(0);
      setModalOpen(true);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setLoadingList(true);
        setError("");

        const list =
          typeof api.getStoriesList === "function"
            ? await api.getStoriesList({ limit: READER_LIST_LIMIT })
            : await api("/api/stories?mode=list");
        if (!mountedRef.current) return;

        const arr = normalizeStoriesListResponse(list);
        setStories(arr);
        arr.forEach((story) => putStoryInCache(story, false));

        if (isAuthenticated && arr.length) {
          hydrateShelfForList(arr);
        }

        const urlTarget = String(initialTargetRef.current || "").trim();

        let lastId = "";
        try {
          lastId = String(localStorage.getItem("ns.reader.lastStoryId") || "").trim();
        } catch {
          lastId = "";
        }

        const preferred = urlTarget || lastId;

        if (!didAutoOpenRef.current && preferred) {
          didAutoOpenRef.current = true;

          const found = arr.find(
            (s) => String(getId(s)) === String(preferred) || String(s?.id) === String(preferred)
          );
          if (found && getId(found)) {
            await openStory(getId(found), { deliberate: false });
          } else {
            await openStory(preferred, { deliberate: false });
          }
        }
      } catch {
        if (!mountedRef.current) return;
        setError(uiText.failedToLoadStorylines);
        setStories([]);
      } finally {
        if (!mountedRef.current) return;
        setLoadingList(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;

    if (!isAuthenticated) {
      setShelfMap({});
      setShelfError("");
      setShelfBusyId("");
      setShelfHydrating(false);
      aliasGroupsRef.current = {};
      return;
    }

    if (Array.isArray(stories) && stories.length > 0) {
      hydrateShelfForList(stories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, stories]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateFollow() {
      setFollowError("");

      const publicationInfo = selectedStory ? getReaderPublicationInfo(selectedStory, lang) : null;
      if (!isAuthenticated || !selectedStory || publicationInfo?.isPublished) {
        setFollowingSelected(false);
        setFollowLoading(false);
        return;
      }

      const id = String(selectedStory?._id || selectedStory?.id || "").trim();
      if (!id) return;

      try {
        setFollowLoading(true);
        const following = await getFollowStatus(id);
        if (!cancelled) setFollowingSelected(following);
      } catch (e) {
        if (!cancelled) {
          setFollowingSelected(false);
          setFollowError(
            safeText(e?.message) ||
              (isFR
                ? "Impossible de vérifier le suivi de cette histoire."
                : "Could not check whether you follow this story.")
          );
        }
      } finally {
        if (!cancelled) setFollowLoading(false);
      }
    }

    hydrateFollow();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, selectedStory, isFR, lang]);

  async function toggleFollowForSelected() {
    if (!isAuthenticated) {
      if (typeof onNavigatePage === "function") onNavigatePage("login");
      return;
    }

    const id = String(selectedStory?._id || selectedStory?.id || "").trim();
    if (!id || followLoading || followBusy || getReaderPublicationInfo(selectedStory, lang)?.isPublished) return;

    try {
      setFollowError("");
      setFollowBusy(true);

      if (followingSelected) {
        await unfollowStory(id);
        setFollowingSelected(false);
      } else {
        await followStory(id);
        setFollowingSelected(true);
      }
    } catch (e) {
      setFollowError(
        safeText(e?.message) ||
          (isFR
            ? "Impossible de modifier le suivi de cette histoire."
            : "Could not change the follow setting for this story.")
      );
    } finally {
      setFollowBusy(false);
    }
  }

  async function toggleShelfForSelected() {
    if (!isAuthenticated) {
  setShelfError(t.loginToUseShelf);
  if (typeof onNavigatePage === "function") {
    onNavigatePage("login");
  }
  return;
}
if (!selectedStory) return;

    const aliases = getIdAliases(selectedStory);
    const primary = String(selectedStory?._id || selectedStory?.id || "").trim();
    if (!primary) return;

    if (selectedShelfState === "unknown") return;

    try {
      setShelfError("");
      setShelfBusyId(primary);

      const currentlySaved = selectedShelfState === "saved";

      if (currentlySaved) {
        let removed = false;
        for (const sid of aliases.length ? aliases : [primary]) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await removeFromShelf(sid);
            removed = true;
            break;
          } catch {}
        }
        if (!removed) throw new Error(t.shelfRemoveFailed);

        if (!mountedRef.current) return;
        aliases.forEach((a) => mirrorShelfValueToAliases(a, false));
      } else {
        let added = false;
        for (const sid of aliases.length ? aliases : [primary]) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await addToShelf(sid);
            added = true;
            break;
          } catch {}
        }
        if (!added) throw new Error(t.shelfAddFailed);

        if (!mountedRef.current) return;
        aliases.forEach((a) => mirrorShelfValueToAliases(a, true));
      }
    } catch (e) {
      if (!mountedRef.current) return;
      const msg = safeText(e?.message) || uiText.shelfActionFailed;

      if (String(msg).toLowerCase().includes("route not found") || String(msg).toLowerCase().includes("404")) {
setShelfError(uiText.shelfRouteMismatch);
      } else {
        setShelfError(msg);
      }
    } finally {
      if (mountedRef.current) setShelfBusyId("");
    }
  }

  const filteredStories = useMemo(() => {
    const arr = Array.isArray(stories) ? stories.slice() : [];
    return arr.filter((s) => storyMatchesQuery(s, searchQuery) && storyMatchesMode(s, modeFilter));
  }, [stories, searchQuery, modeFilter]);

  const sortedStories = useMemo(() => {
    const arr = filteredStories.slice();
    if (!selectedId) return arr;

    arr.sort((a, b) => {
      const ia = String(getId(a) || "");
      const ib = String(getId(b) || "");
      if (ia === String(selectedId) && ib !== String(selectedId)) return -1;
      if (ib === String(selectedId) && ia !== String(selectedId)) return 1;
      return 0;
    });

    return arr;
  }, [filteredStories, selectedId]);

  const displayedStories = useMemo(() => {
    return sortedStories.slice(0, visibleListCount);
  }, [sortedStories, visibleListCount]);

  const hasMoreStories = displayedStories.length < sortedStories.length;

  useEffect(() => {
    if (!selectedId) return;
    if (loadingList) return;
    if (modalOpen) return;

    const container = listScrollRef.current;
    const el = selectedItemRef.current;
    if (!el) return;

    if (didAutoScrollRef.current) return;
    didAutoScrollRef.current = true;

    const timer = window.setTimeout(() => {
      try {
        const canScrollInside =
          !!container &&
          container.scrollWidth > container.clientWidth + 20 &&
          getComputedStyle(container).overflowX !== "visible";

        if (canScrollInside) {
          const cLeft = container.scrollLeft;
          const cRight = cLeft + container.clientWidth;
          const eLeft = el.offsetLeft;
          const eRight = eLeft + el.offsetWidth;
          const pad = 18;
          if (eLeft < cLeft + pad || eRight > cRight - pad) {
            const target = Math.max(0, eLeft - Math.floor(container.clientWidth * 0.18));
            container.scrollTo({ left: target, behavior: "smooth" });
            return;
          }
        }
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
      } catch {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        } catch {}
      }
    }, 60);

    return () => window.clearTimeout(timer);
  }, [selectedId, loadingList, modalOpen, sortedStories.length]);

  const validatedCount = useMemo(() => {
    if (!selectedStory) return 0;
    return approvedCanonScenes(selectedStory).length;
  }, [selectedStory, uiText]);

  const last5 = useMemo(() => {
    if (!selectedStory) return [];
    return lastFiveValidatedScenes(selectedStory);
  }, [selectedStory]);

  const firstScene = useMemo(() => {
    if (!selectedStory) return null;
    return firstCanonScene(selectedStory);
  }, [selectedStory]);

  const modalScenes = useMemo(() => {
    if (modalMode === "first") return firstScene ? [firstScene] : [];
    return last5;
  }, [modalMode, firstScene, last5]);

  const currentModalScene = useMemo(() => {
    if (!Array.isArray(modalScenes) || modalScenes.length === 0) return null;
    const safeIndex = Math.min(Math.max(modalSceneIndex, 0), modalScenes.length - 1);
    return modalScenes[safeIndex] || null;
  }, [modalScenes, modalSceneIndex]);

  const currentModalSceneId = useMemo(() => sceneRatingSceneId(currentModalScene), [currentModalScene]);

  // Keep each scene at its own beginning. The viewport is reused across
  // Previous/Next; reset before paint and before completion tracking observes it.
  useLayoutEffect(() => {
    if (!modalOpen) return;
    const viewport = modalScrollRef.current;
    if (viewport) viewport.scrollTop = 0;
  }, [modalOpen, modalMode, modalSceneIndex, selectedId, currentModalSceneId]);

  function sceneRatingKeyFor(sceneLike) {
    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    return sid && scid ? `${sid}:${scid}` : "";
  }

  function sceneProgressKeyFor(sceneLike, action) {
    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    const a = String(action || "").trim().toLowerCase();
    return sid && scid && a ? `${sid}:${scid}:${a}` : "";
  }

  function recordSceneProgress(sceneLike, action) {
    if (!isAuthenticated) return;

    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    const a = String(action || "").trim().toLowerCase();
    if (!sid || !scid || !a) return;

    const dedupeKey = sceneProgressKeyFor(sceneLike, a);
    const shouldDedupe = a === "opened" || a === "completed";
    if (shouldDedupe && sceneProgressSentRef.current.has(dedupeKey)) return;
    if (shouldDedupe) sceneProgressSentRef.current.add(dedupeKey);

    try {
      if (typeof api.recordStorySceneProgress === "function") {
        api.recordStorySceneProgress(sid, scid, {
          surface: "readerView",
          action: a,
          sceneNumber: sceneLike?.__canonSceneNumber || sceneLike?.sceneNumber || null,
          sceneTitle: safeText(sceneLike?.title || ""),
        }).then(() => {
          bumpTrackingStamp();
        }).catch(() => {
          if (shouldDedupe) sceneProgressSentRef.current.delete(dedupeKey);
        });
      }
    } catch {
      if (shouldDedupe) sceneProgressSentRef.current.delete(dedupeKey);
    }
  }

  function handleModalSceneNavigation(direction) {
    const d = String(direction || "").toLowerCase() === "previous" ? "previous" : "next";
    recordSceneProgress(currentModalScene, d);
    setModalSceneIndex((i) => {
      if (d === "previous") return Math.max(0, i - 1);
      return Math.min(modalScenes.length - 1, i + 1);
    });
  }

  function mergeSceneRatingSummary(sceneLike, summaryLike = {}) {
    const key = sceneRatingKeyFor(sceneLike);
    if (!key) return;

    const sceneId = sceneRatingSceneId(sceneLike);
    const localRating = !isAuthenticated ? readLocalSceneRating(selectedId, sceneId) : null;
    const incoming = summaryLike && typeof summaryLike === "object" ? summaryLike : {};
    const myRating = Number(incoming.myRating || 0) || localRating || null;

    setSceneRatingSummaries((prev) => ({
      ...(prev || {}),
      [key]: {
        ...(prev?.[key] || {}),
        ...incoming,
        myRating,
      },
    }));
  }

  async function fetchSceneRatingSummary(sceneLike) {
    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    const key = sceneRatingKeyFor(sceneLike);
    if (!sid || !scid || !key) return;

    const localRating = !isAuthenticated ? readLocalSceneRating(sid, scid) : null;
    if (localRating) {
      mergeSceneRatingSummary(sceneLike, { myRating: localRating, myRatingKind: "guest" });
    }

    try {
      const path = `/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}/rating-summary`;
      const data = await api(path, { method: "GET" });
      if (!mountedRef.current) return;
      mergeSceneRatingSummary(sceneLike, data?.summary || {});
    } catch {
      // Ratings are useful, but reading must never fail because ratings failed.
    }
  }

  async function saveSceneRating(sceneLike, ratingValue) {
    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    const key = sceneRatingKeyFor(sceneLike);
    const rating = Number(ratingValue);
    if (!sid || !scid || !key || !Number.isInteger(rating) || rating < 1 || rating > 5) return;

    writeLocalSceneRating(sid, scid, rating);
    mergeSceneRatingSummary(sceneLike, { myRating: rating, myRatingKind: isAuthenticated ? "verified" : "guest" });

    setSceneRatingStatus((prev) => ({ ...(prev || {}), [key]: "saving" }));

    try {
      const visitorToken = getOrCreateSceneRatingVisitorToken();
      const path = `/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}/rating`;
      const data = await api(path, {
        method: "POST",
        body: JSON.stringify({ rating, visitorToken }),
      });

      if (!mountedRef.current) return;
      mergeSceneRatingSummary(sceneLike, data?.summary || { myRating: rating });
      setSceneRatingStatus((prev) => ({ ...(prev || {}), [key]: "saved" }));
    } catch {
      if (!mountedRef.current) return;
      setSceneRatingStatus((prev) => ({ ...(prev || {}), [key]: "error" }));
    }
  }


  function mergeSceneReactionSummary(sceneLike, summaryLike = {}) {
    const key = sceneRatingKeyFor(sceneLike);
    if (!key) return;

    const sceneId = sceneRatingSceneId(sceneLike);
    const localReactions = !isAuthenticated ? readLocalSceneReactions(selectedId, sceneId) : [];
    const incoming = summaryLike && typeof summaryLike === "object" ? summaryLike : {};
    const incomingMine = normalizeSceneReactionList(incoming.myReactions);
    const myReactions = incomingMine.length ? incomingMine : localReactions;

    setSceneReactionSummaries((prev) => ({
      ...(prev || {}),
      [key]: {
        ...(prev?.[key] || {}),
        ...incoming,
        myReactions,
      },
    }));
  }

  async function fetchSceneReactionSummary(sceneLike) {
    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    const key = sceneRatingKeyFor(sceneLike);
    if (!sid || !scid || !key) return;

    const localReactions = !isAuthenticated ? readLocalSceneReactions(sid, scid) : [];
    if (localReactions.length) {
      mergeSceneReactionSummary(sceneLike, { myReactions: localReactions, myReactionKind: "guest" });
    }

    try {
      const path = `/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}/reaction-summary`;
      const data = await api(path, { method: "GET" });
      if (!mountedRef.current) return;
      mergeSceneReactionSummary(sceneLike, data?.summary || {});
    } catch {
      // Guided reactions are useful, but reading must never fail because reactions failed.
    }
  }

  async function saveSceneReaction(sceneLike, reactionCodeValue) {
    const sid = String(selectedId || "").trim();
    const scid = sceneRatingSceneId(sceneLike);
    const key = sceneRatingKeyFor(sceneLike);
    const reactionCode = normalizeSceneReactionCode(reactionCodeValue);
    if (!sid || !scid || !key || !reactionCode) return;

    const current = normalizeSceneReactionList(sceneReactionSummaries?.[key]?.myReactions);
    const selected = !current.includes(reactionCode);
    const nextReactions = selected
      ? normalizeSceneReactionList([...current, reactionCode])
      : current.filter((code) => code !== reactionCode);

    writeLocalSceneReactions(sid, scid, nextReactions);
    mergeSceneReactionSummary(sceneLike, {
      myReactions: nextReactions,
      myReactionKind: isAuthenticated ? "verified" : "guest",
    });

    setSceneReactionStatus((prev) => ({ ...(prev || {}), [key]: "saving" }));

    try {
      const visitorToken = getOrCreateSceneRatingVisitorToken();
      const path = `/api/stories/${encodeURIComponent(sid)}/scenes/${encodeURIComponent(scid)}/reaction`;
      const data = await api(path, {
        method: "POST",
        body: JSON.stringify({ reactionCode, selected, visitorToken }),
      });

      if (!mountedRef.current) return;
      mergeSceneReactionSummary(sceneLike, data?.summary || { myReactions: nextReactions });
      setSceneReactionStatus((prev) => ({ ...(prev || {}), [key]: "saved" }));
    } catch {
      if (!mountedRef.current) return;
      setSceneReactionStatus((prev) => ({ ...(prev || {}), [key]: "error" }));
    }
  }


  useEffect(() => {
    if (!modalOpen) return;
    if (!isAuthenticated) return;
    if (!selectedId) return;

    const scene = currentModalScene || null;
    const sceneId = scene?._id || scene?.id || "";
    const canonUpdatedAt =
      safeStr(selectedStory?.canonUpdatedAt) ||
      safeStr(scene?.updatedAt) ||
      safeStr(scene?.createdAt) ||
      "";

    postTrackingProgress(selectedId, { sceneId, scrollY: 0, canonUpdatedAt });
    recordSceneProgress(scene, "opened");
    bumpTrackingStamp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, modalMode, modalSceneIndex, selectedId, currentModalScene, selectedStory, isAuthenticated]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!selectedId) return;
    if (!currentModalSceneId) return;
    fetchSceneRatingSummary(currentModalScene);
    fetchSceneReactionSummary(currentModalScene);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, selectedId, currentModalSceneId, isAuthenticated]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!isAuthenticated) return;
    if (!selectedId) return;
    if (!currentModalSceneId) return;

    const root = modalScrollRef.current;
    const sentinel = sceneBottomRef.current;
    if (!root || !sentinel) return;

    let marked = false;
    let observer = null;

    const markCompleted = () => {
      if (marked) return;
      marked = true;
      recordSceneProgress(currentModalScene, "completed");
    };

    const checkCompletion = () => {
      try {
        const rootRect = root.getBoundingClientRect();
        const sentRect = sentinel.getBoundingClientRect();
        if (sentRect.top <= rootRect.bottom + 12) {
          markCompleted();
        }
      } catch {}
    };

    if (typeof IntersectionObserver !== "undefined") {
      try {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) markCompleted();
          },
          { root, threshold: 0.2 }
        );
        observer.observe(sentinel);
      } catch {
        observer = null;
      }
    }

    const timer = window.setTimeout(checkCompletion, 180);
    root.addEventListener("scroll", checkCompletion, { passive: true });
    window.addEventListener("resize", checkCompletion);

    return () => {
      window.clearTimeout(timer);
      root.removeEventListener("scroll", checkCompletion);
      window.removeEventListener("resize", checkCompletion);
      if (observer) observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, selectedId, currentModalSceneId, isAuthenticated]);

  const selectedPublicationInfo = useMemo(() => {
    return selectedStory ? getReaderPublicationInfo(selectedStory, lang) : { isPublished: false };
  }, [selectedStory, lang]);

  const selectedIsPublished = !!selectedPublicationInfo?.isPublished;

  const selectedStructured = useMemo(() => {
    if (!selectedStory) return null;

    const baseTitle = safeText(selectedStory?.title) || uiText.untitledStoryline;
    const title = selectedPublicationInfo?.isPublished
      ? safeText(selectedPublicationInfo.publishedTitle) || baseTitle
      : baseTitle;
    const subtitle = selectedPublicationInfo?.isPublished
      ? safeText(selectedPublicationInfo.publishedSubtitle || selectedStory?.publicationSubtitle)
      : safeText(selectedStory?.subtitle);
    const forum = safeText(selectedStory?.forum) || uiText.unclassifiedForum;
    const writingMode = guessWritingMode(selectedStory);

    const createdAt = selectedStory?.createdAt || "";
    const updatedAt = selectedStory?.updatedAt || selectedStory?.lastUpdatedAt || "";
    const lastActivityAt = storyLastActivityAt(selectedStory) || updatedAt || createdAt;
    const contributors = countStoryContributors(selectedStory);
    const pulseLabel = storyPulseLabel(selectedStory, isFR ? "fr" : "en");
    const directionLabel = storyDirectionLabel(selectedStory, isFR ? "fr" : "en");

    const authorName =
      selectedPublicationInfo?.isPublished
        ? getPublishedAuthorName(selectedStory, guessAuthorName(selectedStory) || uiText.unknownAuthor)
        : guessAuthorName(selectedStory) || uiText.unknownAuthor;
    const aboutAuthor = clampWords(guessAuthorAbout(selectedStory), 150);
    const synopsis = clampWords(guessSynopsis(selectedStory), 150);
    const description = clampWords(guessDescription(selectedStory), 200);

    return { title, subtitle, forum, writingMode, createdAt, updatedAt, lastActivityAt, contributors, pulseLabel, directionLabel, description, authorName, aboutAuthor, synopsis };
  }, [selectedStory, selectedPublicationInfo, isFR, uiText]);

  async function openValidatedModal(resumeReading = false) {
    if (!selectedStory || selectedIsPublished) return;

    const storyId = String(
      selectedStory?._id ||
        selectedStory?.id ||
        selectedId ||
        ""
    ).trim();

    if (!storyId) return;
    // Continue Reading fetches private progress alongside the mandatory fresh
    // story check. Last Five bypasses this extra request to stay fast.
    const resumePromise = resumeReading === true && isAuthenticated
      ? fetchTrackingOne(storyId)
      : Promise.resolve(null);
    const freshStory = await openStory(storyId, {
      deliberate: false,
      force: true,
      nonBlockingTracking: true,
    });
    if (!mountedRef.current || !freshStory || getReaderPublicationInfo(freshStory, lang)?.isPublished) return;

    const freshRequestSeq = storyReqSeqRef.current;
    const savedTracking = await resumePromise;
    if (!mountedRef.current || storyReqSeqRef.current !== freshRequestSeq) return;
    const authorizedStoryId = String(getId(freshStory) || "").trim();
    const matchingTracking = isAuthenticated &&
      String(savedTracking?.tracking?.storyId || "").trim() === authorizedStoryId;
    const lastOpenedSceneId = matchingTracking
      ? String(savedTracking?.tracking?.readerResumeSceneId || "").trim()
      : "";
    const sceneIndex = resumeReading === true && lastOpenedSceneId
      ? lastFiveValidatedScenes(freshStory).findIndex(
          (scene) => sceneRatingSceneId(scene) === lastOpenedSceneId
        )
      : -1;

    setModalMode("last5");
    setModalSceneIndex(sceneIndex >= 0 ? sceneIndex : 0);
    setModalOpen(true);
  }

  function openFirstSceneModal() {
    if (!firstScene || selectedIsPublished) return;
    setModalMode("first");
    setModalSceneIndex(0);
    setModalOpen(true);
  }

  function closeValidatedModal() {
    setModalOpen(false);
    setModalSceneIndex(0);
  }

  function clearSearch() {
    setSearchInput("");
    setSearchQuery("");
    setModeFilter("all");
  }

  function scrollStoryShelf(direction) {
    const el = listScrollRef.current;
    if (!el) return;
    const amount = Math.max(320, Math.floor(el.clientWidth * 0.72));
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  }

  const guidanceLine = useMemo(() => {
    if (!isAuthenticated) {
      return t.readerViewGuidanceGuest;
    }
    const n = safeText(user?.name);
    return `${t.readerViewGuidanceUser}${n ? ` ${t.welcome}, ${n}.` : ""}`;
  }, [isAuthenticated, user, t.readerViewGuidanceGuest, t.readerViewGuidanceUser, t.welcome]);

  const shelfButtonLabel = useMemo(() => {
    if (!isAuthenticated) return t.loginToUseShelf;
    if (!selectedStory) return t.shelf;
    if (selectedShelfState === "unknown") return t.checking;
    return selectedShelfState === "saved" ? t.removeFromShelf : t.saveToShelf;
  }, [
  isAuthenticated,
  selectedStory,
  selectedShelfState,
  t.loginToUseShelf,
  t.shelf,
  t.checking,
  t.removeFromShelf,
  t.saveToShelf,
]);

  const shelfButtonTitle = useMemo(() => {
    if (!isAuthenticated) return uiText.loginToUseShelfTitle;
    if (!selectedStory) return uiText.selectStoryFirst;
    if (selectedShelfState === "unknown") return uiText.shelfStatusLoading;
    return selectedShelfState === "saved" ? uiText.removeFromShelfTitle : uiText.saveToShelfTitle;
  }, [isAuthenticated, selectedStory, selectedShelfState, uiText]);

  const trackingLastOpenedText = useMemo(() => {
    if (!isAuthenticated) return "";
    if (trackingLoading) return uiText.trackingLoading;
    return formatTrackingLastOpened(trackingSummary, isFR ? "fr" : "en", uiText);
  }, [isAuthenticated, trackingLoading, trackingSummary, isFR, uiText]);

  const showLeftSkeletons = loadingList && sortedStories.length === 0;

  // Step 6: keep right panel calm while list is still booting
  const showRightSkeletons = (!selectedStory && loadingList) || (!selectedStory && loadingStory);

  const readerTabItems = useMemo(() => {
    if (selectedIsPublished) {
      return [
        { key: "story", label: isFR ? "Informations du livre" : "Book Details" },
        { key: "about", label: isFR ? "Auteur & synopsis" : "Author & Synopsis" },
      ];
    }

    return [
      { key: "reading", label: isFR ? "Lecture" : "Reading" },
      { key: "story", label: isFR ? "Storyline" : "Storyline" },
      { key: "about", label: isFR ? "Auteur & synopsis" : "Author & Synopsis" },
      { key: "participate", label: isFR ? "Participer" : "Participate" },
    ];
  }, [isFR, selectedIsPublished]);

  useEffect(() => {
    if (selectedIsPublished) {
      if (modalOpen) setModalOpen(false);
      if (readerViewTab === "reading" || readerViewTab === "participate") {
        setReaderViewTab("story");
      }
    }
  }, [selectedIsPublished, readerViewTab, modalOpen]);

  const readerHeroBg = (process.env.PUBLIC_URL || "") + "/images/storyhub/storyhub-background.png";
  const readerHeroNumber = selectedStory
    ? selectedIsPublished
      ? "✓"
      : validatedCount
    : sortedStories.length;
  const readerHeroLabel = selectedStory
    ? selectedIsPublished
      ? (selectedPublicationInfo?.publicationLabel || (isFR ? "Publié" : "Published"))
      : (isFR ? "scènes validées" : "validated scenes")
    : (isFR ? "storylines disponibles" : "available storylines");

  return (
    <div className="ns-page ns-reader-room">
      {projectHubContext?.projectId ? (
        <ProjectBreadcrumbTrail
          onNavigatePage={onNavigatePage}
          lang={lang}
          pageKey="readerView"
          projectTitle={
            selectedStory?.title ||
            projectHubContext?.projectTitle
          }
        />
      ) : (
        <nav
          className="ns-breadcrumb-trail"
          aria-label={
            isFR
              ? "Fil d’Ariane"
              : "Breadcrumb"
          }
        >
          {typeof onNavigatePage === "function" ? (
            <button
              type="button"
              className="ns-breadcrumb-link"
              onClick={() =>
                onNavigatePage("home")
              }
            >
              {t.home ||
                (isFR
                  ? "ACCUEIL"
                  : "HOME")}
            </button>
          ) : (
            <span className="ns-breadcrumb-current">
              {t.home ||
                (isFR
                  ? "ACCUEIL"
                  : "HOME")}
            </span>
          )}

          <span className="ns-breadcrumb-separator">
            /
          </span>

          <span className="ns-breadcrumb-current">
            Reader View
          </span>
        </nav>
      )}

      <header
        className="ns-page-header ns-identity-hero"
        style={{
          backgroundImage:
            `linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("${readerHeroBg}")`,
        }}
      >
        <div className="ns-identity-hero-copy">
          <p className="ns-identity-kicker ns-gold-kicker">NEXTSCENES</p>

          <h1 className="ns-identity-title">Reader View</h1>

          <p className="ns-identity-subtitle">{t.readerViewSubtitle}</p>

          {error ? (
            <div className="ns-identity-actions">
              <span className="ns-identity-status is-error">{error}</span>
            </div>
          ) : null}
        </div>

        <aside className="ns-identity-hero-stat" aria-label={readerHeroLabel}>
          <span className="ns-identity-stat-number">{readerHeroNumber}</span>
          <span className="ns-identity-stat-label">{readerHeroLabel}</span>
        </aside>
      </header>

      <section className="ns-card ns-reader-search-panel" aria-label={t.searchStorylines || t.storylines}>
        <div className="ns-reader-search-head">
          <div>
            <h2 className="ns-section-title" style={{ marginBottom: 4 }}>
              {t.storylines}
            </h2>
            <p className="ns-card-meta">
              {isAuthenticated ? (shelfHydrating ? uiText.shelfSyncing : uiText.shelfReady) : uiText.guestsCanRead}
            </p>
          </div>

          <div className="ns-card-meta ns-reader-search-count">
            {t.showing} <b>{displayedStories.length}</b> {t.of} <b>{sortedStories.length}</b> {t.matched}
          </div>
        </div>

        {shelfError ? (
          <p className="ns-card-meta" style={{ color: "#b91c1c", marginTop: 8 }}>
            {shelfError}
          </p>
        ) : null}

        <div className="ns-reader-search-controls">
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t.searchPlaceholder}
            className="ns-input ns-reader-search-input"
          />

          <select
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
            className="ns-input ns-reader-mode-select"
            title={t.filterByMode}
          >
            <option value="all">{t.allModes}</option>
            <option value="collaborative">{t.collaborative}</option>
            <option value="solo">{t.solo}</option>
            <option value="group_open">{t.groupOpen}</option>
            <option value="group_closed">{t.groupClosed}</option>
          </select>

          <button className="ns-button ns-button-outline" type="button" onClick={clearSearch} title={t.clearSearch}>
            {t.clear}
          </button>
        </div>
      </section>

      <section className="ns-card ns-reader-story-shelf" aria-label={t.storylines}>
        <div className="ns-reader-shelf-head">
          <div>
            <h2 className="ns-section-title" style={{ marginBottom: 4 }}>
              {isFR ? "Storylines disponibles" : "Available Storylines"}
            </h2>
            <p className="ns-card-meta">
              {isAuthenticated
                ? uiText.shelfLibraryNotFeed.replace("{NAME}", safeText(user?.name) ? `, ${safeText(user?.name)}` : "")
                : uiText.guestsCanRead}
            </p>
          </div>

          <div className="ns-reader-shelf-arrows" aria-label={isFR ? "Navigation des storylines" : "Storyline navigation"}>
            <button
              type="button"
              className="ns-reader-shelf-arrow"
              onClick={() => scrollStoryShelf(-1)}
              title={isFR ? "Faire défiler à gauche" : "Scroll left"}
            >
              ‹
            </button>
            <button
              type="button"
              className="ns-reader-shelf-arrow"
              onClick={() => scrollStoryShelf(1)}
              title={isFR ? "Faire défiler à droite" : "Scroll right"}
            >
              ›
            </button>
          </div>
        </div>

        <div ref={listScrollRef} className="ns-reader-horizontal-list">
          {showLeftSkeletons ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonListItem key={`ls-${i}`} />)
          ) : !loadingList && sortedStories.length === 0 ? (
            <p className="ns-card-meta">{t.noStorylinesFound}</p>
          ) : (
            displayedStories.map((s) => {
              const id = getId(s);
              const isActive = Boolean(id && selectedId && String(id) === String(selectedId));

              const baseTitle = safeText(s?.title) || uiText.untitledStoryline;
              const forum = safeText(s?.forum) || uiText.unclassifiedForum;
              const writingMode = guessWritingMode(s);
              const publicationInfo = getReaderPublicationInfo(s, lang);
              const title = publicationInfo?.isPublished
                ? safeText(publicationInfo.publishedTitle) || baseTitle
                : baseTitle;
              const subtitle = publicationInfo?.isPublished
                ? safeText(publicationInfo.publishedSubtitle || s?.publicationSubtitle)
                : safeText(s?.subtitle);
              const synopsis = publicationInfo?.isPublished
                ? getReaderPublishedCardText(lang)
                : clampWords(guessDescription(s), 12);
              const authorName = publicationInfo?.isPublished
                ? getReaderPublishedAuthorName(s, publicationInfo)
                : guessAuthorName(s);

              const aliases = getIdAliases(s);
              let state = "unknown";
              if (!isAuthenticated) state = "not_saved";
              else {
                for (const a of aliases) {
                  if (shelfMap[a] === true) {
                    state = "saved";
                    break;
                  }
                  if (shelfMap[a] === false) state = "not_saved";
                }
              }

              const disableOpen = !id || loadingStory;

              return (
                <article
                  key={String(id || title)}
                  ref={isActive ? selectedItemRef : null}
                  className={
                    "ns-reader-story-tile" +
                    (isActive ? " is-active" : "") +
                    (publicationInfo?.isPublished ? " is-published" : "")
                  }
                >
                  <button
                    type="button"
                    onClick={() => openStoryAndMaybeModal(id, false)}
                    disabled={disableOpen}
                    aria-disabled={disableOpen ? "true" : "false"}
                    className="ns-reader-story-tile-button"
                    title={!id ? t.missingStoryId : loadingStory ? t.loading : uiText.openStorylineHint}
                  >
                    <div className="ns-reader-story-tile-titleRow">
                      <h3 className="ns-reader-story-tile-title">{title}</h3>
                      {isActive ? <span className="ns-reader-story-selected">{t.selected}</span> : null}
                    </div>
                    {subtitle ? (
                      <p className="ns-reader-story-tile-meta" style={{ marginTop: 4, fontStyle: "italic" }}>
                        {subtitle}
                      </p>
                    ) : null}

                    <div className="ns-reader-story-tile-pills">
                      {!publicationInfo.isPublished ? <ModePill mode={writingMode} t={t} style={{ fontSize: 12, padding: "4px 8px", boxShadow: "none" }} /> : null}
                      <VisibilityStatusPill story={s} lang={lang} style={{ fontSize: 12, padding: "4px 8px" }} />
                      {isAuthenticated && id ? (
                        <ShelfPill
                          state={state}
                          t={t}
                          style={{ padding: "4px 8px", fontSize: 12, fontWeight: 800, boxShadow: "none" }}
                        />
                      ) : null}
                      <PublicationStatusPill info={publicationInfo} />
                    </div>

                    <p className="ns-reader-story-tile-meta">
                      {t.by} {authorName || uiText.unknownAuthor} · {t.forum_labels?.[forum] || forum}
                    </p>

                    {publicationInfo.isPublished ? (
                      <p className="ns-reader-story-tile-meta">
                        {isFR ? "Livre complété · informations du livre" : "Completed book · book details"}
                      </p>
                    ) : (
                      <p className="ns-reader-story-tile-meta" title={formatDateTime(s?.updatedAt || s?.createdAt)}>
                        {t.lastUpdated} {formatRelativeTime(s?.updatedAt || s?.createdAt, lang)}
                      </p>
                    )}

                    <p className="ns-reader-story-tile-synopsis">
                      {synopsis || uiText.openStorylineHint}
                    </p>
                  </button>

                  <button
                    className={
                      "ns-button ns-reader-lastfive-btn" +
                      (publicationInfo?.isPublished ? " ns-button-outline ns-reader-preview-btn" : " ns-button-primary")
                    }
                    type="button"
                    onClick={() => openStoryAndMaybeModal(id, !publicationInfo?.isPublished)}
                    disabled={disableOpen}
                    aria-disabled={disableOpen ? "true" : "false"}
                    title={publicationInfo?.isPublished ? (isFR ? "Voir les informations du livre" : "View book details") : uiText.openLastFiveHint}
                  >
                    {publicationInfo?.isPublished
                      ? (isFR ? "Voir le livre publié" : "View Published Book")
                      : (loadingStory ? t.loadingValidatedScenes : t.lastFiveScenes)}
                  </button>
                  {publicationInfo.isPublished && publicationInfo.amazonHref ? (
                    <button
                      className="ns-button ns-button-outline ns-amazon-action"
                      type="button"
                      onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
                    >
                      {publicationInfo.cta}
                    </button>
                  ) : null}
                </article>
              );
            })
          )}

          {!showLeftSkeletons && hasMoreStories ? (
            <div className="ns-reader-loadmore-tile">
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => setVisibleListCount((prev) => prev + READER_LIST_PAGE_SIZE)}
                title={uiText.showMoreStorylinesTitle}
              >
                {uiText.loadMoreStorylines}
              </button>
            </div>
          ) : null}
        </div>
      </section>

        {/* RIGHT */}
        <section className="ns-card ns-reader-reading-panel" key={String(selectedId || "empty")} style={{ minHeight: 240 }}>

          {showRightSkeletons ? (
            <SkeletonRightPanel />
          ) : !selectedStory ? (
            <div className="ns-reader-orientationCard">
              <div className="ns-reader-orientationTitle">{uiText.welcomeToReaderView}</div>

              <p className="ns-card-meta" style={{ marginBottom: 10 }}>
                {uiText.unfoldOneScene}
              </p>

              <p className="ns-card-meta" style={{ marginBottom: 12 }}>
                {uiText.returnLater}
              </p>

              <div className="ns-reader-orientationPaths">
                <button
                  className="btn"
                  type="button"
                  onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("storyHub") : null)}
                  style={{ minHeight: 40 }}
                >
                  {uiText.storyHubStart}
                </button>

                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    try {
                      const remembered = String(
                        localStorage.getItem("ns.reader.lastStoryId") ||
                          localStorage.getItem("ns.selectedStoryId") ||
                          ""
                      ).trim();
                      if (remembered) {
                        localStorage.setItem("ns.writer.focusStoryId", remembered);
                        localStorage.setItem("ns.selectedStoryId", remembered);
                      }
                    } catch {}
                    if (typeof onNavigatePage === "function") onNavigatePage("writerStudio");
                  }}
                  style={{ minHeight: 40 }}
                >
                  {lastOpenedStoryId ? uiText.writerStudioContinue : uiText.writerStudioWriteSubmit}
                </button>

                {!isAuthenticated && (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("login") : null)}
                    style={{ minHeight: 40 }}
                  >
                    {t.loginToParticipate}
                  </button>
                )}
              </div>

              <p className="ns-card-meta" style={{ marginTop: 12, opacity: 0.78 }}>
                {uiText.selectStoryToBegin}
              </p>
            </div>
          ) : (
            <>
              <div className="ns-reader-selectedHero">
                <div className="ns-reader-selectedMain">
                  {(() => {
                    const publicationInfo = getReaderPublicationInfo(selectedStory, lang);
                    const coverUrl = String(
                      selectedStory?.coverImageUrl ||
                        selectedStory?.coverUrl ||
                        selectedStory?.coverImage ||
                        selectedStory?.imageUrl ||
                        publicationInfo.coverUrl ||
                        ""
                    ).trim();
                    if (coverUrl) {
                      return (
                        <div className="ns-reader-bookCover" aria-hidden="true">
                          <img src={coverUrl} alt="" loading="lazy" />
                        </div>
                      );
                    }
                    return (
                      <div className="ns-reader-bookMark" aria-hidden="true">
                        {initialsFromName(selectedStructured?.title || selectedStory?.title || uiText.storylineLabel)}
                      </div>
                    );
                  })()}

                  <div className="ns-reader-selectedCopy">
                    <p className="ns-reader-kicker">
                      {t.forum_labels?.[selectedStructured?.forum] || selectedStructured?.forum || t.unclassifiedForum}
                    </p>

                    <h2 className="ns-reader-selectedTitle">
                      {selectedStructured?.title || selectedStory?.title || uiText.storylineLabel}
                      {loadingStory ? ` · ${uiText.loadingFullStory}` : ""}
                    </h2>
                    {selectedStructured?.subtitle ? (
                      <p className="ns-card-meta" style={{ margin: "5px 0 0", fontStyle: "italic", fontSize: 16, color: "#475569" }}>
                        {selectedStructured.subtitle}
                      </p>
                    ) : null}

                    <div className="ns-reader-authorLine">
                      {(() => {
                        const avatarUrl = guessAvatarUrl(selectedStory);
                        const name = selectedStructured?.authorName || uiText.unknownAuthor;
                        if (avatarUrl) {
                          return (
                            <img
                              src={avatarUrl}
                              alt={name}
                              className="ns-reader-authorAvatar"
                            />
                          );
                        }
                        return (
                          <span className="ns-reader-authorAvatar ns-reader-authorAvatarText" title={name}>
                            {initialsFromName(name)}
                          </span>
                        );
                      })()}
                      <span>{t.by} {selectedStructured?.authorName || uiText.unknownAuthor}</span>
                    </div>
                  </div>
                </div>

                <div className="ns-reader-selectedControls">
                  {!selectedIsPublished ? (
                    <ModePill mode={selectedStructured?.writingMode || selectedStory?.writingMode} t={t} />
                  ) : null}
                  <VisibilityStatusPill story={selectedStory} lang={lang} />
                  <ShelfPill state={selectedShelfState} t={t} />
                  {(() => {
                    const publicationInfo = selectedPublicationInfo;
                    if (!publicationInfo.isPublished) return null;
                    return (
                      <>
                        <PublicationStatusPill info={publicationInfo} />
                        {!selectedIsPublished ? <PreviewStatusPill info={publicationInfo} /> : null}
                        {publicationInfo.amazonHref ? (
                          <button
                            className="btn ns-amazon-action"
                            type="button"
                            onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
                            style={{ minHeight: 38 }}
                          >
                            {publicationInfo.cta}
                          </button>
                        ) : null}
                      </>
                    );
                  })()}
                  {!selectedIsPublished ? (
                    <button
                      className="btn"
                      type="button"
                      onClick={toggleFollowForSelected}
                      disabled={!selectedStory || followLoading || followBusy}
                      aria-pressed={followingSelected ? "true" : "false"}
                      title={
                        followingSelected
                          ? isFR
                            ? "Ne plus recevoir les nouvelles scènes de cette histoire."
                            : "Stop receiving new-scene alerts for this story."
                          : isFR
                            ? "Recevoir une alerte lorsqu’une nouvelle scène est publiée."
                            : "Receive an alert when a new scene is published."
                      }
                      style={{ minHeight: 38 }}
                    >
                      {followBusy
                        ? uiText.working
                        : followLoading
                          ? t.checking
                          : followingSelected
                            ? isFR
                              ? "Histoire suivie"
                              : "Following"
                            : isFR
                              ? "Suivre cette histoire"
                              : "Follow this story"}
                    </button>
                  ) : null}
                  <button
                    className="btn"
                    type="button"
                    onClick={toggleShelfForSelected}
                    disabled={
                      !selectedStory ||
                      selectedShelfState === "unknown" ||
                      shelfBusyId === String(selectedStory?._id || selectedStory?.id || "")
                    }
                    aria-disabled={
                      !selectedStory ||
                      selectedShelfState === "unknown" ||
                      shelfBusyId === String(selectedStory?._id || selectedStory?.id || "")
                        ? "true"
                        : "false"
                    }
                    title={shelfButtonTitle}
                    style={{ minHeight: 38 }}
                  >
                    {shelfBusyId === String(selectedStory?._id || selectedStory?.id || "") ? uiText.working : shelfButtonLabel}
                  </button>
                </div>

                {!selectedIsPublished && followError && (
                  <p className="ns-card-meta" style={{ color: "#b91c1c", marginTop: 10 }}>
                    {followError}
                  </p>
                )}

                {shelfError && (
                  <p className="ns-card-meta" style={{ color: "#b91c1c", marginTop: 10 }}>
                    {shelfError}
                  </p>
                )}
              </div>

              {!selectedIsPublished ? (
                <p className="ns-reader-visibility-note">
                  {getReaderVisibilityCopy(getStoryVisibilityStatus(selectedStory), lang).note}
                </p>
              ) : null}

              {selectedIsPublished ? (
                <PublishedReaderNotice
                  story={selectedStory}
                  publicationInfo={selectedPublicationInfo}
                  lang={lang}
                />
              ) : null}

              <div className="ns-reader-tabs" role="tablist" aria-label={isFR ? "Sections de Reader View" : "Reader View sections"}>
                {readerTabItems.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={readerViewTab === tab.key ? "true" : "false"}
                    className={"ns-reader-tab" + (readerViewTab === tab.key ? " is-active" : "")}
                    onClick={() => setReaderViewTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="ns-reader-tabPanel">
                {readerViewTab === "reading" && !selectedIsPublished && (
                  <div className="ns-reader-readingDesk">
                    <div className="ns-reader-sectionHead">
                      <div>
                        <h2 className="ns-section-title" style={{ marginBottom: 3 }}>
                          {t.storyBegins || uiText.openingSceneTitle}
                        </h2>
                        <p className="ns-card-meta">{uiText.validatedReadingWindow}</p>
                      </div>
                      <button
                        className="btn"
                        type="button"
                        onClick={() => openValidatedModal(true)}
                        disabled={!selectedStory || loadingStory}
                        style={{ minHeight: 40 }}
                      >
                        {loadingStory ? t.loading : t.continueReading}
                      </button>
                    </div>

                    <div className="ns-reader-openingText">
                      {firstScene ? (
                        <>
                          <div className="ns-reader-openingInner">
                            {(() => {
                              const text = safeText(firstScene?.text);
                              if (!text) return t.noOpeningScene;
                              return openingSceneExcerpt(text, 150);
                            })()}
                          </div>

                          <div className="ns-reader-inlineActions">
                            <button className="btn" type="button" onClick={openFirstSceneModal} style={{ minHeight: 38 }}>
                              {t.readOpening}
                            </button>
                            <button className="btn" type="button" onClick={() => openValidatedModal(false)} disabled={loadingStory} style={{ minHeight: 38 }}>
                              {loadingStory ? t.loadingValidatedScenes : t.lastFiveScenes}
                            </button>
                          </div>
                        </>
                      ) : (
                        <p className="ns-card-meta">{t.noOpeningScene}</p>
                      )}
                    </div>
                  </div>
                )}

                {readerViewTab === "story" && (
                  <div className="ns-reader-storyFacts">
                    {selectedIsPublished ? (
                      <div className="ns-published-reader-facts" role="note">
                        <strong>{isFR ? "Statut" : "Status"}</strong>
                        <span>
                          {isFR
                            ? "Ce livre est complété et publié. Les informations de développement actif ont été retirées de Reader View."
                            : "This book is completed and published. Active-development details have been removed from Reader View."}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="ns-reader-factsGrid">
                          <div className="ns-reader-factCard">
                            <span>{t.dateTimeCreated}</span>
                            <strong>{formatDateTime(selectedStructured?.createdAt) || uiText.notAvailable}</strong>
                          </div>
                          <div className="ns-reader-factCard">
                            <span>{t.lastUpdated}</span>
                            <strong>{formatDateTime(selectedStructured?.updatedAt) || uiText.notAvailable}</strong>
                          </div>
                          <div className="ns-reader-factCard">
                            <span>{isFR ? "Pouls de l’histoire" : "Story pulse"}</span>
                            <strong>{selectedStructured?.pulseLabel || uiText.notAvailable}</strong>
                          </div>
                          <div className="ns-reader-factCard">
                            <span>{isFR ? "Contributeurs" : "Contributors"}</span>
                            <strong>{selectedStructured?.contributors || 0}</strong>
                          </div>
                          <div className="ns-reader-factCard">
                            <span>{isFR ? "Dernière activité" : "Last activity"}</span>
                            <strong>{selectedStructured?.lastActivityAt ? formatRelativeTime(selectedStructured.lastActivityAt, isFR ? "fr" : "en") : uiText.notAvailable}</strong>
                          </div>
                          <div className="ns-reader-factCard">
                            <span>{isFR ? "Direction actuelle" : "Current direction"}</span>
                            <strong>{selectedStructured?.directionLabel || uiText.notAvailable}</strong>
                          </div>
                        </div>

                        {isAuthenticated ? (
                          <div className="ns-continuity-callout" title={trackingLastOpenedText} style={{ marginTop: 12 }}>
                            <div className="ns-continuity-icon" aria-hidden="true">↻</div>
                            <div className="ns-continuity-body">
                              <div className="ns-continuity-kicker">{uiText.trackingLastOpenedLabel}</div>
                              <div className="ns-continuity-main">{trackingLastOpenedText}</div>
                              <div className="ns-continuity-helper">{uiText.trackingLastOpenedHelper}</div>
                            </div>
                          </div>
                        ) : null}
                      </>
                    )}

                    <div className="ns-reader-descriptionBlock">
                      <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                        <strong>{t.description}</strong>
                      </p>
                      <div style={{ whiteSpace: "pre-wrap", color: "#0f172a", lineHeight: "1.62" }}>
                        {selectedStructured?.description || t.noDescriptionYet}
                      </div>
                    </div>
                  </div>
                )}

                {readerViewTab === "about" && (
                  <div className="ns-reader-aboutGrid">
                    <div className="ns-reader-textBlock">
                      <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                        <strong>{t.aboutTheAuthor}</strong>
                      </p>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {selectedStructured?.aboutAuthor ? selectedStructured.aboutAuthor : t.noAuthorBioYet}
                      </div>
                    </div>

                    <div className="ns-reader-textBlock">
                      <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                        <strong>{t.synopsis}</strong>
                      </p>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {selectedStructured?.synopsis ? selectedStructured.synopsis : t.noSynopsisYet}
                      </div>
                    </div>
                  </div>
                )}

                {readerViewTab === "participate" && (
                  <div className="ns-reader-participationCard">
                    {(() => {
                      const participation = getParticipationGuidance(selectedStory);
                      return (
                        <>
                          <p className="ns-card-meta" style={{ marginBottom: 6 }}>
                            <strong>{participation.title}</strong>
                          </p>
                          <div className="ns-card-meta" style={{ lineHeight: "1.6", color: "#334155", marginBottom: 10 }}>
                            {participation.body}
                          </div>
                          <p className="ns-card-meta" style={{ marginBottom: 0 }}>
                            {uiText.safeParticipation}
                          </p>
                          <div className="ns-reader-inlineActions" style={{ marginTop: 12 }}>
                            <button
                              className="btn"
                              type="button"
                              onClick={() => {
                                try {
                                  if (selectedId) {
                                    localStorage.setItem("ns.writer.focusStoryId", String(selectedId));
                                    localStorage.setItem("ns.selectedStoryId", String(selectedId));
                                  }
                                } catch {}
                                if (typeof onNavigatePage === "function") {
                                  onNavigatePage(isAuthenticated ? "writerStudio" : "login");
                                }
                              }}
                              style={{ minHeight: 40 }}
                            >
                              {isAuthenticated ? participation.action : t.loginToParticipate}
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

      {/* MODAL */}
      {modalOpen && !selectedIsPublished && (
        <div
          className="ns-reader-modalOverlay"
          role="dialog"
          aria-modal="true"
          onClick={closeValidatedModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            className="ns-card ns-reader-modalCard"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "min(86vh, calc(100dvh - 24px))",
              overflow: "hidden",
              contain: "layout paint",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <h2 className="ns-section-title" style={{ marginBottom: 0 }}>
                {modalMode === "first" ? uiText.openingSceneTitle : t.lastFiveScenes}
              </h2>

              <button className="btn" type="button" onClick={closeValidatedModal} title={t.close} style={{ minHeight: 40 }}>
                {t.close}
              </button>
            </div>

            <p className="ns-card-meta" style={{ marginTop: 6 }}>
              {selectedStructured?.title ? `${uiText.fromLabel}: ${selectedStructured.title}` : uiText.selectedStoryLabel}
            </p>

            <p
              className="ns-card-meta"
              style={{
                marginTop: 8,
                marginBottom: 0,
                padding: "8px 10px",
                borderRadius: 12,
                background: "rgba(2, 44, 34, 0.05)",
                border: "1px solid rgba(2, 44, 34, 0.10)",
              }}
            >
              {modalMode === "first"
                ? uiText.openingSceneContext
                : uiText.lastFiveContext}
            </p>

            <div
              ref={modalScrollRef}
              className="ns-reader-modalScroll"
              style={{
                marginTop: 10,
                flex: "1 1 auto",
                minHeight: 0,
                maxHeight: "none",
                overflowY: "auto",
                paddingRight: 6,
                paddingBottom: "calc(18px + env(safe-area-inset-bottom, 0px))",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {!selectedStory ? (
                <p className="ns-card-meta">{t.noSelectedStory}</p>
              ) : loadingStory && modalScenes.length === 0 ? (
                <p className="ns-card-meta">{t.loadingValidatedScenes}</p>
              ) : modalScenes.length === 0 || !currentModalScene ? (
                <p className="ns-card-meta">{modalMode === "first" ? uiText.noOpeningScene : t.noValidatedScenesYet}</p>
              ) : (
                <article
                  key={currentModalScene?._id || `${selectedId}-${modalMode}-${modalSceneIndex}`}
                  className="ns-card ns-card-soft ns-reader-sceneCard"
                  style={{ marginBottom: 12 }}
                >
                  <div className="ns-reader-sceneMetaRow">
                    <p className="ns-card-meta ns-reader-sceneMeta">
                      {modalMode === "first"
                        ? `${uiText.openingSceneMeta} · ${formatDateTime(currentModalScene?.createdAt)}`
                        : `${uiText.sceneLabel} ${currentModalScene?.__canonSceneNumber || modalSceneIndex + 1} ${t.of} ${currentModalScene?.__canonSceneTotal || modalScenes.length} · ${formatDateTime(currentModalScene?.createdAt)}`}
                    </p>
                  </div>

                  <ReaderSceneIllustration scene={currentModalScene} t={t} />

                  <div
                    className="ns-reader-text ns-reader-copyProtected"
                    onContextMenu={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <div className="ns-reader-textInner" style={{ whiteSpace: "pre-wrap" }}>
                      <ChildrensBookManuscriptReadOnly
                        content={currentModalScene?.manuscriptContent || null}
                        fallbackText={safeText(currentModalScene?.text)}
                        lang={isFR ? "fr" : "en"}
                      />
                    </div>
                  </div>

                  <div className="ns-reader-copyNotice" aria-label={uiText.readerCopyProtectionNotice}>
                    <span aria-hidden="true">©</span>
                    <span>{uiText.readerCopyProtectionNotice}</span>
                  </div>

                  <div className="ns-reader-ratingNavRow">
                    <SceneRatingStrip
                      storyId={selectedId}
                      scene={currentModalScene}
                      summary={sceneRatingSummaries[sceneRatingKeyFor(currentModalScene)] || {}}
                      status={sceneRatingStatus[sceneRatingKeyFor(currentModalScene)] || ""}
                      isAuthenticated={isAuthenticated}
                      labels={{
                        prompt: uiText.sceneRatingPrompt,
                        saved: uiText.sceneRatingSaved,
                        guestSaved: uiText.sceneRatingGuestSaved,
                        saving: uiText.sceneRatingSaving,
                        error: uiText.sceneRatingError,
                        aria: uiText.sceneRatingAria,
                        current: uiText.sceneRatingCurrent,
                        summaryLabel: uiText.sceneRatingSummaryLabel,
                        summaryEmpty: uiText.sceneRatingSummaryEmpty,
                        summarySingular: uiText.sceneRatingSummarySingular,
                        summaryPlural: uiText.sceneRatingSummaryPlural,
                        summaryAria: uiText.sceneRatingSummaryAria,
                      }}
                      onRate={saveSceneRating}
                    />

                    <SceneReactionStrip
                      storyId={selectedId}
                      scene={currentModalScene}
                      summary={sceneReactionSummaries[sceneRatingKeyFor(currentModalScene)] || {}}
                      status={sceneReactionStatus[sceneRatingKeyFor(currentModalScene)] || ""}
                      isAuthenticated={isAuthenticated}
                      labels={{
                        prompt: uiText.sceneReactionPrompt,
                        saved: uiText.sceneReactionSaved,
                        guestSaved: uiText.sceneReactionGuestSaved,
                        saving: uiText.sceneReactionSaving,
                        error: uiText.sceneReactionError,
                        aria: uiText.sceneReactionAria,
                        current: uiText.sceneReactionCurrent,
                        reactions: uiText.sceneReactionLabels || {},
                      }}
                      onToggleReaction={saveSceneReaction}
                    />

                    {modalMode === "last5" && modalScenes.length > 1 && (
                      <div className="ns-reader-bottomNav" aria-label={`${uiText.previous} / ${uiText.next}`}>
                        <button
                          className="btn ns-reader-bottomNavBtn"
                          type="button"
                          onClick={() => handleModalSceneNavigation("previous")}
                          disabled={modalSceneIndex <= 0}
                          aria-disabled={modalSceneIndex <= 0 ? "true" : "false"}
                        >
                          {uiText.previous}
                        </button>
                        <button
                          className="btn ns-reader-bottomNavBtn"
                          type="button"
                          onClick={() => handleModalSceneNavigation("next")}
                          disabled={modalSceneIndex >= modalScenes.length - 1}
                          aria-disabled={modalSceneIndex >= modalScenes.length - 1 ? "true" : "false"}
                        >
                          {uiText.next}
                        </button>
                      </div>
                    )}
                  </div>

                  <div ref={sceneBottomRef} className="ns-reader-sceneCompletionSentinel" aria-hidden="true" />
                </article>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ns-reader-room .ns-page-header { margin-bottom: 18px; }
        .ns-reader-search-panel,
        .ns-reader-story-shelf,
        .ns-reader-reading-panel {
          margin-bottom: 18px;
          border-radius: 24px;
          border: 1px solid rgba(211, 181, 115, 0.28);
          background:
            radial-gradient(circle at top right, rgba(226, 201, 145, 0.10), transparent 32%),
            linear-gradient(180deg, rgba(255, 253, 247, 0.96) 0%, rgba(248, 244, 235, 0.92) 100%);
          box-shadow: 0 14px 36px rgba(15, 23, 42, 0.08);
        }

        .ns-reader-search-head,
        .ns-reader-shelf-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        .ns-reader-search-count {
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(236, 253, 245, 0.86);
          border: 1px solid rgba(16, 185, 129, 0.22);
          color: #065f46;
          font-weight: 800;
          white-space: nowrap;
        }

        .ns-reader-search-controls {
          margin-top: 14px;
          display: grid;
          grid-template-columns: minmax(260px, 1.2fr) minmax(220px, 0.55fr) auto;
          gap: 12px;
          align-items: center;
        }

        .ns-reader-search-input,
        .ns-reader-mode-select {
          max-width: none;
          min-height: 44px;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.14);
          background: rgba(255, 255, 255, 0.92);
          padding: 10px 14px;
        }

        .ns-reader-shelf-arrows {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .ns-reader-shelf-arrow {
          width: 40px;
          height: 40px;
          border-radius: 999px;
          border: 1px solid rgba(11, 112, 61, 0.42);
          background: rgba(255, 250, 240, 0.92);
          color: #064e3b;
          font-size: 26px;
          font-weight: 900;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
        }

        .ns-reader-shelf-arrow:hover {
          background: #fffaf0;
          border-color: rgba(11, 112, 61, 0.72);
          transform: translateY(-1px);
        }

        .ns-reader-horizontal-list {
          margin-top: 14px;
          display: flex;
          gap: 16px;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-behavior: smooth;
          scroll-snap-type: x proximity;
          padding: 3px 2px 14px;
          overscroll-behavior-x: contain;
          -webkit-overflow-scrolling: touch;
        }

        .ns-reader-story-tile {
          flex: 0 0 310px;
          width: 310px;
          height: 340px;
          min-height: 340px;
          box-sizing: border-box;
          scroll-snap-align: start;
          display: flex;
          flex-direction: column;
          padding: 14px;
          border-radius: 20px;
          border: 1px solid rgba(15, 23, 42, 0.10);
          background:
            radial-gradient(circle at top right, rgba(226, 201, 145, 0.12), transparent 34%),
            linear-gradient(180deg, #ffffff 0%, #fbf8f1 100%);
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
        }

        .ns-reader-story-tile.is-active {
          border-color: rgba(11, 112, 61, 0.86);
          box-shadow: 0 0 0 4px rgba(11, 112, 61, 0.12), 0 16px 34px rgba(15, 23, 42, 0.12);
        }

        .ns-reader-story-tile-button {
          width: 100%;
          min-height: 0;
          text-align: left;
          background: transparent;
          border: 0;
          padding: 0;
          cursor: pointer;
          color: inherit;
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          align-items: stretch;
        }

        .ns-reader-story-tile-button:disabled {
          cursor: not-allowed;
          opacity: 0.72;
        }

        .ns-reader-story-tile-titleRow {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 8px;
        }

        .ns-reader-story-tile-title {
          margin: 0;
          font-family: Georgia, "Times New Roman", serif;
          font-size: 18px;
          line-height: 1.14;
          font-weight: 900;
          letter-spacing: -0.02em;
          color: #111827;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .ns-reader-story-selected {
          flex: 0 0 auto;
          padding: 4px 8px;
          border-radius: 999px;
          background: rgba(236, 253, 245, 0.92);
          border: 1px solid rgba(16, 185, 129, 0.26);
          color: #065f46;
          font-size: 11px;
          font-weight: 900;
        }

        .ns-reader-story-tile-pills {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }

        .ns-reader-story-tile-meta,
        .ns-reader-story-tile-synopsis {
          margin: 0 0 6px;
          color: rgba(15, 23, 42, 0.72);
          font-size: 13.5px;
          line-height: 1.38;
        }

        .ns-reader-story-tile-meta {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ns-reader-story-tile-synopsis {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          margin-bottom: auto;
        }

        .ns-reader-lastfive-btn {
          margin-top: 8px;
          min-height: 34px;
          align-self: flex-start;
          padding: 6px 12px;
          font-size: 13px;
        }

        .ns-reader-loadmore-tile {
          flex: 0 0 220px;
          min-height: 238px;
          display: grid;
          place-items: center;
          border-radius: 20px;
          border: 1px dashed rgba(11, 112, 61, 0.32);
          background: rgba(255, 255, 255, 0.56);
        }

        .ns-reader-reading-panel {
          min-height: 520px;
        }

        @media (max-width: 780px) {
          .ns-reader-search-controls {
            grid-template-columns: 1fr;
          }
          .ns-reader-story-tile {
            flex-basis: 82vw;
            width: 82vw;
            height: auto;
            min-height: 238px;
          }
        }

        @media (max-width: 1180px) {
          .ns-reader-grid {
            grid-template-columns: minmax(350px, 1.08fr) minmax(0, 1.92fr) !important;
          }
        }

        @media (max-width: 980px) {
          .ns-reader-grid {
            grid-template-columns: 1fr !important;
          }
        }

        .ns-reader-modalCard { background: #ffffff; }
        .ns-reader-modalScroll { padding: 6px 4px 10px 0; }

        .ns-reader-sceneCard {
          background: rgba(248, 250, 252, 0.72);
          border: 1px solid rgba(15, 23, 42, 0.10);
        }

        .ns-reader-text {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.10);
          border-radius: 16px;
          padding: 18px 18px;
        }

        .ns-reader-copyProtected,
        .ns-reader-copyProtected * {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          -webkit-touch-callout: none;
        }

        .ns-reader-copyNotice {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 8px;
          padding: 5px 9px;
          border-radius: 999px;
          border: 1px solid rgba(6, 95, 70, 0.14);
          background: rgba(236, 253, 245, 0.62);
          color: rgba(6, 78, 59, 0.82);
          font-size: 12px;
          font-weight: 700;
          line-height: 1.2;
        }

        .ns-reader-textInner {
          max-width: 720px;
          margin: 0 auto;
          font-family: Georgia, "Times New Roman", Times, serif;
          font-size: 18px;
          line-height: 1.78;
          letter-spacing: 0.01em;
          word-spacing: 0.02em;
          color: #111827;
        }

        @media (max-width: 520px) {
          .ns-reader-text { padding: 14px 14px; }
          .ns-reader-textInner { font-size: 16.5px; line-height: 1.75; }
        }
      `}</style>
    </div>
  );
}
